/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import ISPGenerator from './components/ISPGenerator.jsx';
import {
    buildSectionMarkLookup,
    calculateSubjectResult,
    getFoundationOverallPossible,
    getFoundationSubjectDefinition
} from './foundationPhase';

// --- SVG Icons ---
const Icons = {
    Chart: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
    Check: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Save: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>,
    Print: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>,
    Trash: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
};

const normalizeGroupText = (value) => String(value || '').trim();

const parsePositiveNumber = (value, fallback = 1) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getAssessmentGroupKey = (section) => {
    const assessmentGroup = normalizeGroupText(section?.assessment_group);
    if (assessmentGroup) {
        return `group:${assessmentGroup.toLowerCase()}`;
    }
    const category = normalizeGroupText(section?.category);
    if (category) {
        return `category:${category.toLowerCase()}`;
    }
    return `section:${section?.id}`;
};

const getAssessmentGroupLabel = (section) => {
    const assessmentGroup = normalizeGroupText(section?.assessment_group);
    if (assessmentGroup) {
        return assessmentGroup;
    }
    const category = normalizeGroupText(section?.category);
    if (category) {
        return category;
    }
    return section?.section_name || 'Assessment';
};

const buildAssessmentGroups = (sections) => {
    const groups = new Map();
    (sections || []).forEach((section) => {
        const key = getAssessmentGroupKey(section);
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                label: getAssessmentGroupLabel(section),
                sections: [],
                rawMax: 0,
                weights: new Set(),
                primaryGroupName: normalizeGroupText(section?.assessment_group),
                usesCategoryFallback: !normalizeGroupText(section?.assessment_group) && !!normalizeGroupText(section?.category)
            });
        }
        const group = groups.get(key);
        group.sections.push(section);
        group.rawMax += parseFloat(section?.max_mark) || 0;
        group.weights.add(parsePositiveNumber(section?.weight, 1));
    });

    return Array.from(groups.values())
        .map((group) => ({
            ...group,
            currentWeight: group.weights.size > 0 ? Array.from(group.weights)[0] : 1,
            hasMixedWeights: group.weights.size > 1
        }))
        .sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' }));
};

const buildExcelSectionDescriptor = (section, groupLabelOverride = '') => {
    const sectionLabel = normalizeGroupText(section?.section_name || 'Assessment');
    const groupLabel = normalizeGroupText(groupLabelOverride || getAssessmentGroupLabel(section));

    return {
        sectionLabel,
        groupLabel,
        normalizedSectionLabel: sectionLabel.toLowerCase(),
        normalizedGroupLabel: groupLabel.toLowerCase(),
        compositeKey: `${groupLabel.toLowerCase()}|${sectionLabel.toLowerCase()}`
    };
};

const isGroupedExcelTemplate = (rows) => {
    if (!Array.isArray(rows) || rows.length < 2) {
        return false;
    }

    const primaryHeader = Array.isArray(rows[0]) ? rows[0] : [];
    const secondaryHeader = Array.isArray(rows[1]) ? rows[1] : [];
    const firstCell = normalizeGroupText(primaryHeader[0]).toLowerCase();
    const secondCell = normalizeGroupText(primaryHeader[1]).toLowerCase();

    if (firstCell !== 'first name' || secondCell !== 'last name') {
        return false;
    }

    const secondRowNameCellsBlank = !normalizeGroupText(secondaryHeader[0]) && !normalizeGroupText(secondaryHeader[1]);
    const hasSecondarySectionLabels = secondaryHeader.slice(2).some((value) => normalizeGroupText(value));
    return secondRowNameCellsBlank && hasSecondarySectionLabels;
};

const buildSectionImportLookup = (sectionList, headerMetaById = {}) => {
    const sectionsByCompositeKey = new Map();
    const sectionsByLabel = new Map();

    (sectionList || []).forEach((section) => {
        const descriptor = buildExcelSectionDescriptor(
            section,
            normalizeGroupText(headerMetaById?.[section.id]?.groupLabel || getAssessmentGroupLabel(section))
        );

        if (!sectionsByCompositeKey.has(descriptor.compositeKey)) {
            sectionsByCompositeKey.set(descriptor.compositeKey, []);
        }
        sectionsByCompositeKey.get(descriptor.compositeKey).push(section);

        if (!sectionsByLabel.has(descriptor.normalizedSectionLabel)) {
            sectionsByLabel.set(descriptor.normalizedSectionLabel, []);
        }
        sectionsByLabel.get(descriptor.normalizedSectionLabel).push(section);
    });

    return { sectionsByCompositeKey, sectionsByLabel };
};

const shiftNextSectionMatch = (lookup, key) => {
    const bucket = lookup.get(key);
    if (!bucket || bucket.length === 0) {
        return null;
    }
    return bucket.shift() || null;
};

const buildImportedSectionColumns = (rows, groupedTemplate) => {
    const groupHeader = Array.isArray(rows[0]) ? rows[0] : [];
    const sectionHeader = groupedTemplate && Array.isArray(rows[1]) ? rows[1] : groupHeader;
    const maxColumnCount = Math.max(groupHeader.length, sectionHeader.length);
    const columns = [];

    for (let excelColIndex = 2; excelColIndex < maxColumnCount; excelColIndex += 1) {
        const groupLabel = groupedTemplate ? normalizeGroupText(groupHeader[excelColIndex]) : '';
        const sectionLabel = normalizeGroupText(sectionHeader[excelColIndex]);
        if (!groupLabel && !sectionLabel) {
            continue;
        }

        columns.push({
            excelColIndex,
            groupLabel,
            sectionLabel,
            normalizedSectionLabel: sectionLabel.toLowerCase(),
            compositeKey: `${groupLabel.toLowerCase()}|${sectionLabel.toLowerCase()}`
        });
    }

    return columns;
};

const getSubjectTermStorageKey = (subjectId) => `subjectdetail.currentTerm.${subjectId}`;

const getStoredSubjectTerm = (subjectId) => {
    if (typeof window === 'undefined' || !subjectId) {
        return 1;
    }

    const storedValue = window.localStorage.getItem(getSubjectTermStorageKey(subjectId));
    const parsedTerm = parseInt(storedValue || '', 10);
    return [1, 2, 3, 4].includes(parsedTerm) ? parsedTerm : 1;
};

export default function Subjectdetail() {
    // Modal for resource suggestions/interventions
    const [showResourceModal, setShowResourceModal] = useState(false);
    const [resourceSuggestions, setResourceSuggestions] = useState([]);
    const [loadingResources, setLoadingResources] = useState(false);

    // Fetch resources for the current subject/grade
    const fetchResourceSuggestions = async () => {
        setLoadingResources(true);
        try {
            const token = localStorage.getItem('auth_token');
            const res = await axios.get((process.env.REACT_APP_API_URL || 'http://localhost/api') + '/fetch_library.php', {
                params: { grade: subjectGrade, subject: subjectname },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setResourceSuggestions(res.data.resources || []);
        } catch (e) {
            setResourceSuggestions([]);
        }
        setLoadingResources(false);
    };
    // Collapse state for Assessment Sections
    const [sectionsCollapsed, setSectionsCollapsed] = useState(true);
    // Collapse state for Import/Export and Add Student
    const [importExportCollapsed, setImportExportCollapsed] = useState(true);
    const [addStudentCollapsed, setAddStudentCollapsed] = useState(true);
        // --- Top 10 Students by Percentage ---
        const [showTop10, setShowTop10] = useState(false);
    const { id } = useParams();

    // --- State ---
    const [currentTerm, setCurrentTerm] = useState(() => getStoredSubjectTerm(id));
    const [termReadySubjectId, setTermReadySubjectId] = useState(() => id || '');
    const [students, setStudents] = useState([]);
    const [sections, setSections] = useState([]);
    const [sectionMarks, setSectionMarks] = useState([]);
    const [marksBeingEdited, setMarksBeingEdited] = useState({});
    const [subjectname, setSubjectName] = useState('Loading...');
    const [subjectGrade, setSubjectGrade] = useState('');
    const [subjectClass, setSubjectClass] = useState('');
    const [subjectGradingMode, setSubjectGradingMode] = useState('standard');
    const [foundationTemplateCode, setFoundationTemplateCode] = useState('');
    const [foundationTemplateDefinition, setFoundationTemplateDefinition] = useState(null);
    const [loading, setLoading] = useState(true);

    // UI State
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvProgress, setCsvProgress] = useState('');
    const [saveAllDisabled, setSaveAllDisabled] = useState(false);

    // Forms
    const [newStudent, setNewStudent] = useState({ firstName: '', lastName: '' });
    const [newSection, setNewSection] = useState({ section_name: '', weight: 1, max_mark: 100, category: '', assessment_group: '' });
    const [addingSection, setAddingSection] = useState(false);
    const [assessmentWeightDrafts, setAssessmentWeightDrafts] = useState({});
    const [assessmentNameDrafts, setAssessmentNameDrafts] = useState({});
    const [savingAssessmentNameKey, setSavingAssessmentNameKey] = useState('');
    const [savingAssessmentGroupKey, setSavingAssessmentGroupKey] = useState('');

    // Debounce State
    const [sectionMaxMarks, setSectionMaxMarks] = useState({});

    // Notes State
    const [notes, setNotes] = useState([]);
    const [noteText, setNoteText] = useState('');
    const [isImportant, setIsImportant] = useState(false);
    const [dueDate, setDueDate] = useState('');
    const [savingNote, setSavingNote] = useState(false);

    // Copy Section State
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [otherSubjects, setOtherSubjects] = useState([]);
    const [copyingSection, setCopyingSection] = useState(false);

    // Refs - Initialize once with useRef and useCallback
    const markInputRefs = useRef({});
    const maxMarkTimersRef = useRef({});
    const getMarkInputRef = useCallback((sectionId, studentId) => {
        if (!markInputRefs.current[sectionId]) {
            markInputRefs.current[sectionId] = {};
        }
        if (!markInputRefs.current[sectionId][studentId]) {
            markInputRefs.current[sectionId][studentId] = React.createRef();
        }
        return markInputRefs.current[sectionId][studentId];
    }, []);

    // --- Helpers ---
    const calculateLevel = useCallback((percentage) => {
        const rounded = Math.round(percentage);
        if (rounded >= 80) return 7;
        if (rounded >= 70) return 6;
        if (rounded >= 60) return 5;
        if (rounded >= 50) return 4;
        if (rounded >= 40) return 3;
        if (rounded >= 30) return 2;
        return 1;
    }, []);

    const levelToSymbol = useCallback((level) => {
        switch (level) {
            case 7: return 'A*';
            case 6: return 'A';
            case 5: return 'B';
            case 4: return 'C';
            case 3: return 'D';
            case 2: return 'E';
            case 1: return 'F';
            default: return level;
        }
    }, []);

    const formatGrade = useCallback((level) => {
        const gradeDisplayFormat = localStorage.getItem('gradeDisplayFormat') || 'levels';
        return gradeDisplayFormat === 'symbols' ? levelToSymbol(level) : level;
    }, [levelToSymbol]);

    // --- Calculate Stats Per Section (Optimized with Memoization) ---
    const getSectionStats = useCallback((section) => {
        const validMarks = [];
        const studentIds = new Set(); // Track unique students
        const sectionId = parseInt(section.id);

        for (const mark of sectionMarks) {
            if (parseInt(mark.section_id) === sectionId &&
                mark.mark !== '' &&
                mark.mark != null &&
                !isNaN(parseFloat(mark.mark))) {
                validMarks.push(parseFloat(mark.mark));
                studentIds.add(parseInt(mark.student_id)); // Add unique student ID
            }
        }

        if (validMarks.length === 0) return null;

        const count = studentIds.size; // Count unique students, not marks
        const max = Math.max(...validMarks);
        const min = Math.min(...validMarks);
        const sum = validMarks.reduce((a, b) => a + b, 0);
        const avg = sum / validMarks.length;
        const maxPossible = section.max_mark || 100;
        const avgPerc = (avg / maxPossible) * 100;

        return { count, max, min, avg, avgPerc };
    }, [sectionMarks]);

    // --- NOTES HELPERS ---
    const getDueDateStatus = useCallback((dueDate) => {
        if (!dueDate) return { status: 'future', label: '', daysLeft: null };
        const due = new Date(dueDate);
        const now = new Date();
        const diff = due - now;
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
        
        if (days < 0) return { status: 'overdue', label: 'Overdue', daysLeft: Math.abs(days) };
        if (days === 0) return { status: 'due-today', label: 'Due Today', daysLeft: 0 };
        if (days <= 2) return { status: 'due-soon', label: `Due in ${days} day${days > 1 ? 's' : ''}`, daysLeft: days };
        if (days <= 7) return { status: 'due-week', label: `Due in ${days} days`, daysLeft: days };
        return { status: 'future', label: `Due in ${days} days`, daysLeft: days };
    }, []);

    const handleCreateNote = async (e) => {
        e.preventDefault();
        const cleanText = noteText.trim();
        if (!cleanText) return;

        setSavingNote(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'create',
                subject_id: id,
                note_text: cleanText,
                is_important: isImportant ? 1 : 0,
                due_date: dueDate || null
            });

            if (!res.data.success) {
                alert(res.data.error || 'Failed to save note');
            } else {
                setNoteText('');
                setIsImportant(false);
                setDueDate('');
                // Reload notes
                const notesRes = await axios.get(`${process.env.REACT_APP_API_URL}/subject_notes.php?subject_id=${id}`);
                    setNotes(notesRes.data.notes || []);
                    // Notify other views (notes page, dashboard) to refresh
                    try { window.dispatchEvent(new CustomEvent('notesUpdated', { detail: { subject_id: id } })); } catch (e) {}
            }
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to save note');
        } finally {
            setSavingNote(false);
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm('Delete this note?')) return;
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'delete',
                   note_id: noteId
            });
            if (res.data.success) {
                setNotes(notes.filter(n => n.id !== noteId));
                try { window.dispatchEvent(new CustomEvent('notesUpdated', { detail: { subject_id: id } })); } catch (e) {}
            } else {
                   alert(res.data.error || 'Failed to delete note');
            }
        } catch (error) {
               alert(error.response?.data?.error || 'Failed to delete note');
        }
    };

    const handleToggleNoteDone = async (noteId, currentDone) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/subject_notes.php`, {
                action: 'update',
                note_id: noteId,
                is_done: currentDone ? 0 : 1
            });
            if (res.data.success) {
                setNotes(notes.map(n => n.id === noteId ? { ...n, is_done: currentDone ? 0 : 1 } : n));
                try { window.dispatchEvent(new CustomEvent('notesUpdated', { detail: { subject_id: id } })); } catch (e) {}
            }
        } catch (error) {
            console.error('Failed to update note status');
        }
    };

    // --- COPY SECTION LOGIC ---
    const handleOpenCopyModal = async () => {
        setShowCopyModal(true);
        
        // Fetch other subjects owned by teacher
        try {
            const token = localStorage.getItem('auth_token');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_dashboard.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            // Filter to only other subjects (not current one)
            const others = (res.data.subjects || []).filter(s => String(s.subject_id) !== String(id));
            setOtherSubjects(others);
        } catch (error) {
            alert('Failed to load other subjects');
            setShowCopyModal(false);
        }
    };

    const handleCopyAllAssessments = async (targetSubjectId) => {
        if (!id) {
            alert('Error: Subject ID not found');
            return;
        }
        
        const sourceId = parseInt(id);
        if (isNaN(sourceId)) {
            alert('Error: Invalid subject ID');
            return;
        }
        
        if (!targetSubjectId) {
            alert('Error: Target subject ID not found');
            return;
        }
        
        setCopyingSection(true);
        try {
            const token = localStorage.getItem('auth_token');
            const payload = {
                source_subject_id: sourceId,
                target_subject_id: parseInt(targetSubjectId)
            };
            
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/duplicate_section.php`,
                JSON.stringify(payload),
                {
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            if (res.data.success) {
                alert(`${res.data.message}`);
                setShowCopyModal(false);
                // Could reload sections here if we want, but typically user navigates to target subject
            } else {
                alert(res.data.error || 'Copy failed');
            }
        } catch (error) {
            console.error('Copy error details:', error.response?.data || error.message);
            alert(error.response?.data?.error || 'Failed to copy assessments');
        } finally {
            setCopyingSection(false);
        }
    };

    // --- LOGIC: Grid Navigation (Optimized with cached indexes) ---
    const handleGridNavigation = useCallback((e, currentStudentId, currentSectionId) => {
        const allowedKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];
        if (!allowedKeys.includes(e.key)) return;

        e.preventDefault();

        const studentIndex = students.findIndex(s => String(s.student_id) === String(currentStudentId));
        const sectionIndex = sections.findIndex(s => String(s.id) === String(currentSectionId));

        let nextStudentId = currentStudentId;
        let nextSectionId = currentSectionId;

        if (e.key === 'ArrowDown' || e.key === 'Enter') {
            if (studentIndex < students.length - 1) nextStudentId = students[studentIndex + 1].student_id;
        } else if (e.key === 'ArrowUp') {
            if (studentIndex > 0) nextStudentId = students[studentIndex - 1].student_id;
        } else if (e.key === 'ArrowRight') {
            if (sectionIndex < sections.length - 1) nextSectionId = sections[sectionIndex + 1].id;
        } else if (e.key === 'ArrowLeft') {
            if (sectionIndex > 0) nextSectionId = sections[sectionIndex - 1].id;
        }

        const ref = getMarkInputRef(nextSectionId, nextStudentId);
        if (ref && ref.current) {
            ref.current.focus();
            ref.current.select();
        }
    }, [students, sections, getMarkInputRef]);

    // --- LOGIC: Bulk Save ---
    const handleBulkSave = async () => {
        setSaveAllDisabled(true);
        try {
            // Separate marks into updates and deletes for better handling
            const marksToCreate = [];
            const marksToUpdate = [];
            const marksToDelete = [];

            // Only process marks that were actually edited (in marksBeingEdited)
            Object.entries(marksBeingEdited).forEach(([key, value]) => {
                const [studentId, sectionId] = key.split('-');
                const section = sections.find(s => String(s.id) === String(sectionId));
                const existingMark = sectionMarks.find(m =>
                    parseInt(m.student_id) === parseInt(studentId) &&
                    parseInt(m.section_id) === parseInt(sectionId)
                );

                // Check if mark is empty/null (should be deleted)
                if (value === '' || value === null || value === undefined) {
                    if (existingMark && existingMark.id) {
                        // Has ID - it exists in DB, needs deletion
                        marksToDelete.push(existingMark.id);
                    }
                    // Else: never saved, skip it
                } else {
                    // Has a value - create or update
                    const markData = {
                        student_id: parseInt(studentId),
                        section_id: parseInt(sectionId),
                        subject_id: id,
                        term: currentTerm,
                        mark: value,
                        max_mark: section ? section.max_mark : 100
                    };

                    if (existingMark && existingMark.id) {
                        markData.id = existingMark.id;
                        marksToUpdate.push(markData);
                    } else {
                        marksToCreate.push(markData);
                    }
                }
            });

            let totalUpdated = 0;

            // Handle deletions
            if (marksToDelete.length > 0) {
                for (const markId of marksToDelete) {
                    try {
                        await axios.delete(`${process.env.REACT_APP_API_URL}/section_marks.php?id=${markId}`);
                        totalUpdated++;
                    } catch (e) {
                        console.error(`Failed to delete mark ${markId}:`, e);
                    }
                }
                // Remove deleted marks from state
                setSectionMarks(prev => prev.filter(m => !marksToDelete.includes(m.id)));
            }

            // Handle creates and updates
            if (marksToCreate.length > 0 || marksToUpdate.length > 0) {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/save_bulk_marks.php`, {
                    create: marksToCreate,
                    update: marksToUpdate
                });

                if (response.data && response.data.success) {
                    // Count newly created and updated marks
                    totalUpdated += marksToCreate.length + marksToUpdate.length;

                    // Update marks with returned IDs for newly created marks
                    if (response.data.created_ids && Object.keys(response.data.created_ids).length > 0) {
                        setSectionMarks(prev => prev.map(m => {
                            const key = `${m.student_id}-${m.section_id}`;
                            const newId = response.data.created_ids[key];
                            return newId && !m.id ? { ...m, id: newId } : m;
                        }));
                    }
                } else {
                    alert('Save failed: ' + (response.data?.error || 'Unknown error'));
                }
            }

            alert(`Saved successfully! Updated/deleted ${totalUpdated} marks.`);
        } catch (error) {
            alert('Save error: ' + (error.response?.data?.error || error.message));
        } finally {
            setSaveAllDisabled(false);
        }
    };

    // --- LOGIC: Excel Upload ---
    const handleExcelUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setCsvUploading(true);
        setCsvProgress('Reading file...');

        const reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length < 2) {
                    alert('Excel file is empty or missing data rows');
                    setCsvUploading(false); return;
                }

                const groupedTemplate = isGroupedExcelTemplate(jsonData);
                const sectionColumns = buildImportedSectionColumns(jsonData, groupedTemplate);
                const dataStartRowIndex = groupedTemplate ? 2 : 1;

                if (sectionColumns.length === 0) {
                    alert('Excel must have at least one section column after First Name and Last Name');
                    setCsvUploading(false); return;
                }

                let workingSections = [...sections];
                let sectionLookup = buildSectionImportLookup(workingSections, sectionHeaderMeta);
                const mappedSectionColumns = [];
                let sectionsCreated = 0;
                let skippedColumns = 0;

                setCsvProgress('Analyzing sections...');

                for (const column of sectionColumns) {
                    let existingSection = null;

                    if (column.groupLabel) {
                        existingSection = shiftNextSectionMatch(sectionLookup.sectionsByCompositeKey, column.compositeKey);
                    }

                    if (!existingSection) {
                        existingSection = shiftNextSectionMatch(sectionLookup.sectionsByLabel, column.normalizedSectionLabel);
                    }

                    if (existingSection) {
                        mappedSectionColumns.push({
                            sectionId: existingSection.id,
                            excelColIndex: column.excelColIndex
                        });
                    } else {
                        if (isFoundationSubject) {
                            skippedColumns += 1;
                            continue;
                        }

                        try {
                            const res = await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                                subject_id: id,
                                term: currentTerm,
                                section_name: column.sectionLabel || column.groupLabel || 'Assessment',
                                weight: 1,
                                max_mark: 100,
                                category: column.groupLabel,
                                assessment_group: column.groupLabel
                            });

                            if (res.data.success && res.data.section_id) {
                                const newSection = {
                                    id: res.data.section_id,
                                    section_name: column.sectionLabel || column.groupLabel || 'Assessment',
                                    weight: 1,
                                    max_mark: 100,
                                    category: column.groupLabel,
                                    assessment_group: column.groupLabel
                                };
                                workingSections.push(newSection);
                                mappedSectionColumns.push({
                                    sectionId: newSection.id,
                                    excelColIndex: column.excelColIndex
                                });
                                sectionsCreated++;
                            }
                        } catch (err) { }
                    }
                }

                if (mappedSectionColumns.length === 0) {
                    alert('Could not match sections.'); setCsvUploading(false); return;
                }

                const totalRows = Math.max(jsonData.length - dataStartRowIndex, 0);

                setCsvProgress(`Processing ${totalRows} students...`);

                const batchSize = 10;
                let successCount = 0;
                let studentsCreated = 0;
                const workingStudents = [...students];
                const workingSectionMarks = [...sectionMarks];
                const processStudentRow = async (row) => {
                    const firstName = String(row[0] || '').trim();
                    const lastName = String(row[1] || '').trim();
                    if (!firstName || !lastName) {
                        return;
                    }

                    try {
                        let student = workingStudents.find(s =>
                            s.first_name.toLowerCase() === firstName.toLowerCase() &&
                            s.last_name.toLowerCase() === lastName.toLowerCase()
                        );

                        if (!student) {
                            try {
                                const addRes = await axios.post(`${process.env.REACT_APP_API_URL}/add_student_with_marks.php`, {
                                    subject_id: id, first_name: firstName, last_name: lastName
                                });
                                if (addRes.data.success) {
                                    student = { student_id: addRes.data.student_id, first_name: firstName, last_name: lastName };
                                    workingStudents.push(student);
                                    studentsCreated++;
                                }
                            } catch (e) { return; }
                        }

                        if (student) {
                            for (const { sectionId, excelColIndex } of mappedSectionColumns) {
                                const rawMark = row[excelColIndex];
                                const mark = parseFloat(rawMark);
                                if (!isNaN(mark)) {
                                    const markObj = workingSectionMarks.find(m =>
                                        parseInt(m.student_id) === parseInt(student.student_id) &&
                                        parseInt(m.section_id) === parseInt(sectionId)
                                    );
                                    try {
                                        if (markObj && markObj.id) {
                                            await axios.put(`${process.env.REACT_APP_API_URL}/section_marks.php`, { id: markObj.id, mark: mark });
                                            markObj.mark = mark;
                                        } else {
                                            const saveRes = await axios.post(`${process.env.REACT_APP_API_URL}/section_marks.php`, {
                                                student_id: student.student_id,
                                                section_id: parseInt(sectionId, 10),
                                                mark
                                            });
                                            if (saveRes.data?.success && saveRes.data?.id) {
                                                workingSectionMarks.push({
                                                    id: saveRes.data.id,
                                                    student_id: student.student_id,
                                                    section_id: parseInt(sectionId, 10),
                                                    mark
                                                });
                                            }
                                        }
                                        successCount++;
                                    } catch (err) { }
                                }
                            }
                        }
                    } catch (rowError) { }
                };

                for (let batchStart = dataStartRowIndex; batchStart < jsonData.length; batchStart += batchSize) {
                    const batchEnd = Math.min(batchStart + batchSize, jsonData.length);
                    const batchPromises = [];

                    for (let i = batchStart; i < batchEnd; i++) {
                        const row = jsonData[i];
                        if (!row || row.length < 2) continue;
                        batchPromises.push(processStudentRow(row));
                    }
                    await Promise.all(batchPromises);
                    const processed = Math.min(batchEnd - 1, totalRows);
                    setCsvProgress(`Processing... ${processed}/${totalRows}`);
                    await new Promise(resolve => setTimeout(resolve, 100)); // Rate limit
                }

                setCsvUploading(false);
                setCsvProgress('');

                // Refresh the full subject view so students, totals, sections, and marks all stay in sync.
                try {
                    await loadSubjectData({ showLoader: false });
                } catch (refreshErr) {
                    console.error('Error refreshing data:', refreshErr);
                }

                alert(
                    `Import Result for Term ${currentTerm}:\nNew Sections: ${sectionsCreated}\nNew Students: ${studentsCreated}\nMarks Updated: ${successCount}\nSkipped Columns: ${skippedColumns}`
                );

            } catch (error) {
                setCsvUploading(false);
                alert('Error processing Excel: ' + error.message);
            }
        };
    };

    const foundationSubjectDefinition = useMemo(() => (
        getFoundationSubjectDefinition(
            subjectname,
            subjectGrade,
            subjectGradingMode,
            foundationTemplateCode,
            currentTerm,
            foundationTemplateDefinition
        )
    ), [subjectname, subjectGrade, subjectGradingMode, foundationTemplateCode, currentTerm, foundationTemplateDefinition]);
    const isFoundationSubject = !!foundationSubjectDefinition;
    const foundationWeightsByLabel = useMemo(() => (
        (foundationSubjectDefinition?.components || []).reduce((acc, component) => {
            acc[String(component.label || '').toLowerCase()] = component.weight ?? null;
            return acc;
        }, {})
    ), [foundationSubjectDefinition]);
    const sectionHeaderMeta = useMemo(() => {
        const palette = ['#f4fbf5', '#f8f6eb', '#eef7fb', '#f7f1fb', '#fbf4ef'];
        const groupSizes = (sections || []).reduce((acc, section) => {
            const groupKey = getAssessmentGroupKey(section);
            acc[groupKey] = (acc[groupKey] || 0) + 1;
            return acc;
        }, {});
        let lastGroupKey = '';
        let groupIndex = -1;

        return sections.reduce((acc, section) => {
            const groupLabel = normalizeGroupText(getAssessmentGroupLabel(section));
            const groupKey = getAssessmentGroupKey(section);
            const sectionLabel = normalizeGroupText(section.section_name || 'Assessment');
            const showGroupLabel = isFoundationSubject
                || groupSizes[groupKey] > 1
                || groupLabel.toLowerCase() !== sectionLabel.toLowerCase();
            if (groupKey !== lastGroupKey) {
                groupIndex += 1;
                lastGroupKey = groupKey;
            }

            acc[section.id] = {
                groupLabel,
                groupKey,
                groupIndex,
                tint: showGroupLabel ? palette[groupIndex % palette.length] : undefined,
                showGroupLabel
            };
            return acc;
        }, {});
    }, [isFoundationSubject, sections]);
    const hasGroupedColumnHeaders = useMemo(() => (
        Object.values(sectionHeaderMeta).some((meta) => meta.showGroupLabel)
    ), [sectionHeaderMeta]);
    const studentCalculationMarkLookup = useMemo(() => buildSectionMarkLookup(sectionMarks), [sectionMarks]);

    // --- Computed Data (Optimized - Only recalculates when dependencies change) ---
    const studentsWithComputed = useMemo(() => {
        return students.map(student => {
            const studentId = parseInt(student.student_id, 10);
            let rawSectionMarkSum = 0;
            let rawSectionMaxSum = 0;

            sections.forEach((section) => {
                const markObj = studentCalculationMarkLookup.get(`${studentId}-${section.id}`);
                const mark = (markObj && markObj.mark !== '' && markObj.mark != null)
                    ? parseFloat(markObj.mark)
                    : null;
                const max = parseFloat(section.max_mark) || 100;

                if (mark !== null && !Number.isNaN(mark) && max > 0) {
                    rawSectionMarkSum += mark;
                    rawSectionMaxSum += max;
                }
            });

            const result = calculateSubjectResult({
                subjectName: subjectname,
                subjectGrade,
                gradingMode: subjectGradingMode,
                foundationTemplateCode,
                foundationDefinition: foundationSubjectDefinition,
                term: currentTerm,
                sections,
                markLookup: studentCalculationMarkLookup,
                studentId
            });

            const average = result.percentage;
            const level = average !== null ? calculateLevel(average) : null;

            return {
                ...student,
                average,
                level,
                rawMarkSum: rawSectionMarkSum,
                rawMaxSum: rawSectionMaxSum,
                finalMarkSum: result.totalAchieved,
                finalMaxSum: result.totalPossible,
                isFoundation: result.isFoundation
            };
        });
    }, [students, subjectname, subjectGrade, subjectGradingMode, foundationTemplateCode, foundationSubjectDefinition, currentTerm, sections, studentCalculationMarkLookup, calculateLevel]);

    const top10Students = useMemo(() => {
        return [...studentsWithComputed]
            .filter(s => s.average !== null)
            .sort((a, b) => (b.average ?? -1) - (a.average ?? -1))
            .slice(0, 10);
    }, [studentsWithComputed]);
    const top10Ids = useMemo(() => top10Students.map(s => s.student_id), [top10Students]);

    // --- Bottom 10 (At Risk) Learners ---
    const [showBottom10, setShowBottom10] = useState(false);
    // Learners at risk: 55% and below
    const atRiskLearners = useMemo(() => {
        return [...studentsWithComputed]
            .filter(s => s.average !== null && s.average <= 55)
            .sort((a, b) => (a.average ?? 101) - (b.average ?? 101));
    }, [studentsWithComputed]);
    const assessmentGroups = useMemo(() => buildAssessmentGroups(sections), [sections]);
    const matchingAssessmentGroup = useMemo(() => {
        const normalizedName = normalizeGroupText(newSection.assessment_group).toLowerCase();
        if (!normalizedName) return null;
        return assessmentGroups.find((group) => (
            normalizeGroupText(group.primaryGroupName).toLowerCase() === normalizedName
        )) || null;
    }, [assessmentGroups, newSection.assessment_group]);
    const recordedMarksCount = useMemo(() => {
        return sectionMarks.filter(mark => mark.mark !== '' && mark.mark != null).length;
    }, [sectionMarks]);


    // --- Marks Map for O(1) lookup during render ---
    const marksMap = useMemo(() => {
        const map = new Map();
        for (const mark of sectionMarks) {
            map.set(`${mark.student_id}-${mark.section_id}`, mark);
        }
        return map;
    }, [sectionMarks]);

   


    // --- Excel Export ---
    const handleExportExcel = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();

        const nameLower = (subjectname || '').toLowerCase();
        const isHomeLanguage = /^G[0-3]_HL$/i.test(String(foundationTemplateCode || '').trim()) || nameLower.includes('home language') || nameLower.includes('huistaal');
        const passMark = isHomeLanguage ? 50 : 40;

        const localMarksMap = new Map();
        for (const mark of sectionMarks) {
            localMarksMap.set(`${mark.student_id}-${mark.section_id}`, mark);
        }

        const activeSections = sections.filter(s => parseFloat(s.weight) !== 0);
        const totalMaxMark = foundationSubjectDefinition
            ? getFoundationOverallPossible(foundationSubjectDefinition)
            : activeSections.reduce((sum, s) => sum + (parseFloat(s.max_mark) || 0), 0);

        const thinBorder = {
            top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
            right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
        };

        const fillGrey = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

        const styleRange = (sheet, startRow, endRow, startCol, endCol, style) => {
            for (let r = startRow; r <= endRow; r++) {
                for (let c = startCol; c <= endCol; c++) {
                    const cell = sheet.getCell(r, c);
                    if (style.font) cell.font = { ...(cell.font || {}), ...style.font };
                    if (style.fill) cell.fill = style.fill;
                    if (style.border) cell.border = style.border;
                    if (style.alignment) cell.alignment = { ...(cell.alignment || {}), ...style.alignment };
                }
            }
        };

        // ==================== SHEET 1: MARKS ====================
        const ws1 = workbook.addWorksheet(`Term ${currentTerm} Marks`);

        const categoryDefs = [];
        
        const categoryMap = {};
        activeSections.forEach(s => {
            const categoryName = (s.category || '').trim() || 'Uncategorized';
            if (!categoryMap[categoryName]) {
                categoryMap[categoryName] = {
                    name: categoryName,
                    totalMax: 0,
                    sectionIds: []
                };
                categoryDefs.push(categoryMap[categoryName]);
            }
            categoryMap[categoryName].totalMax += (parseFloat(s.max_mark) || 0);
            categoryMap[categoryName].sectionIds.push(s.id);
        });

        // Compute dimensions upfront (needed for info rows)
        
        const categoryStartCol = 4 + sections.length * 3;
        const overallStartCol = categoryStartCol + categoryDefs.length * 3;
        const ws1TotalCols = overallStartCol + 2;
        const midCol = Math.ceil(ws1TotalCols / 2);

        // Info row 1: Subject | Term
        const infoRow1 = Array(ws1TotalCols).fill('');
        infoRow1[0] = 'Subject:';
        infoRow1[1] = subjectname;
        infoRow1[midCol - 1] = 'Term:';
        infoRow1[midCol] = String(currentTerm);
        ws1.addRow(infoRow1);  // row 1

        // Info row 2: Educator | Grade | Class
        const infoRow2 = Array(ws1TotalCols).fill('');
        infoRow2[0] = 'Educator:';
        infoRow2[midCol - 1] = 'Grade:';
        infoRow2[midCol] = subjectGrade;
        infoRow2[midCol + 1] = 'Class:';
        infoRow2[midCol + 2] = subjectClass;
        ws1.addRow(infoRow2);  // row 2

        const headerRow0 = ['No', 'Surname', 'Name'];
        sections.forEach(s => {
            headerRow0.push(`${s.section_name} (${s.max_mark})`);
            headerRow0.push('');
            headerRow0.push('');
        });
        categoryDefs.forEach(cat => {
            headerRow0.push(`TOTAL ${cat.name.toUpperCase()} (${cat.totalMax})`);
            headerRow0.push('');
            headerRow0.push('');
        });
        headerRow0.push(`OVERALL TOTAL (${totalMaxMark})`);
        headerRow0.push('');
        headerRow0.push('');

        const headerRow1 = ['', '', ''];
        sections.forEach(s => {
            headerRow1.push(s.max_mark);
            headerRow1.push('%');
            headerRow1.push('Lvl');
        });
        categoryDefs.forEach(cat => {
            headerRow1.push(cat.totalMax);
            headerRow1.push('%');
            headerRow1.push('Lvl');
        });
        headerRow1.push(totalMaxMark);
        headerRow1.push('%');
        headerRow1.push('Lvl');

        ws1.addRow(headerRow0);  // row 3
        ws1.addRow(headerRow1);  // row 4

        const secStats = {};
        activeSections.forEach(s => {
            secStats[s.id] = {
                marks: [],
                levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
                passedCount: 0,
                failedCount: 0
            };
        });

        const catStats = {};
        categoryDefs.forEach(cat => {
            catStats[cat.name] = {
                percentages: [],
                levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
                passedCount: 0,
                failedCount: 0
            };
        });

        const overallRawData = [];
        let overallPassedCount = 0;
        let overallFailedCount = 0;
        const overallLevelDist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };

        studentsWithComputed.forEach((student, idx) => {
            const row = [idx + 1, student.last_name, student.first_name];
            const studentId = parseInt(student.student_id);
            const studentCatTotals = {};
            categoryDefs.forEach(cat => {
                studentCatTotals[cat.name] = { markSum: 0, maxSum: 0, hasMark: false };
            });

            sections.forEach(section => {
                const markObj = localMarksMap.get(`${studentId}-${section.id}`);
                const markVal = (markObj && markObj.mark !== '' && markObj.mark != null)
                    ? parseFloat(markObj.mark)
                    : null;
                const max = parseFloat(section.max_mark) || 100;

                row.push(markVal !== null ? markVal : '');

                if (markVal !== null && max > 0) {
                    const p = (markVal / max) * 100;
                    row.push(Math.round(p));
                    const level = calculateLevel(p);
                    row.push(formatGrade(level));

                    if (parseFloat(section.weight) !== 0 && secStats[section.id]) {
                        secStats[section.id].marks.push(markVal);
                        secStats[section.id].levelDist[level]++;
                        if (Math.round(p) >= passMark) secStats[section.id].passedCount++;
                        else secStats[section.id].failedCount++;

                        const categoryName = (section.category || '').trim() || 'Uncategorized';
                        if (categoryName && studentCatTotals[categoryName]) {
                            studentCatTotals[categoryName].markSum += markVal;
                            studentCatTotals[categoryName].maxSum += max;
                            studentCatTotals[categoryName].hasMark = true;
                        }
                    }
                } else {
                    row.push('');
                    row.push('');
                }
            });

            categoryDefs.forEach(cat => {
                const catTotal = studentCatTotals[cat.name];
                if (!catTotal.hasMark || catTotal.maxSum <= 0) {
                    row.push('');
                    row.push('');
                    row.push('');
                    return;
                }

                const catPerc = (catTotal.markSum / catTotal.maxSum) * 100;
                const catLevel = calculateLevel(catPerc);

                row.push(Number(catTotal.markSum).toFixed(1));
                row.push(Math.round(catPerc));
                row.push(formatGrade(catLevel));

                if (catStats[cat.name]) {
                    catStats[cat.name].percentages.push(catPerc);
                    catStats[cat.name].levelDist[catLevel]++;
                    if (Math.round(catPerc) >= passMark) catStats[cat.name].passedCount++;
                    else catStats[cat.name].failedCount++;
                }
            });

            const rawTotal = student.rawMarkSum;
            const rawMax = student.rawMaxSum;
            const calculatedTotal = isFoundationSubject ? student.finalMarkSum : rawTotal;
            const calculatedMax = isFoundationSubject ? student.finalMaxSum : rawMax;
            const overallPerc = student.average;

            row.push(calculatedMax > 0 ? Number(calculatedTotal).toFixed(1) : '');
            row.push(overallPerc !== null ? Math.round(overallPerc) : '');
            const overallLevel = overallPerc !== null ? calculateLevel(overallPerc) : null;
            row.push(overallLevel ? formatGrade(overallLevel) : '');

            if (overallPerc !== null) {
                overallRawData.push({ markSum: calculatedTotal, maxSum: calculatedMax, perc: overallPerc });
                overallLevelDist[overallLevel]++;
                if (Math.round(overallPerc) >= passMark) overallPassedCount++;
                else overallFailedCount++;
            }

            ws1.addRow(row);
        });

        // Info row merges
        if (midCol > 2) ws1.mergeCells(1, 2, 1, midCol - 1);
        if (midCol + 1 < ws1TotalCols) ws1.mergeCells(1, midCol + 1, 1, ws1TotalCols);
        if (midCol > 2) ws1.mergeCells(2, 2, 2, midCol - 1);
        if (midCol + 3 <= ws1TotalCols) ws1.mergeCells(2, midCol + 3, 2, ws1TotalCols);

        // Column header merges (rows 3-4)
        ws1.mergeCells(3, 1, 4, 1);
        ws1.mergeCells(3, 2, 4, 2);
        ws1.mergeCells(3, 3, 4, 3);
        sections.forEach((s, i) => {
            const sc = 4 + i * 3;
            ws1.mergeCells(3, sc, 3, sc + 2);
        });
        categoryDefs.forEach((cat, i) => {
            const cc = categoryStartCol + i * 3;
            ws1.mergeCells(3, cc, 3, cc + 2);
        });
        ws1.mergeCells(3, overallStartCol, 3, overallStartCol + 2);

        ws1.columns = [
            { width: 5 },
            { width: 18 },
            { width: 16 },
            ...sections.flatMap(() => ([{ width: 10 }, { width: 6 }, { width: 6 }])),
            ...categoryDefs.flatMap(() => ([{ width: 12 }, { width: 6 }, { width: 6 }])),
            { width: 12 },
            { width: 6 },
            { width: 6 }
        ];

        const ws1LastRow = ws1.rowCount;

        // Style info rows (1-2)
        styleRange(ws1, 1, 2, 1, ws1TotalCols, {
            fill: fillGrey('FFFAFAFA'),
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'middle' }
        });
        styleRange(ws1, 1, 2, 1, 1, { font: { bold: true } });
        styleRange(ws1, 1, 1, midCol, midCol, { font: { bold: true } });
        styleRange(ws1, 2, 2, midCol, midCol, { font: { bold: true } });
        styleRange(ws1, 2, 2, midCol + 2, midCol + 2, { font: { bold: true } });
        ws1.getRow(1).height = 22;
        ws1.getRow(2).height = 22;

        // Style column header rows (3-4)
        styleRange(ws1, 3, 3, 1, ws1TotalCols, {
            font: { bold: true },
            fill: fillGrey('FFD9D9D9'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        styleRange(ws1, 4, 4, 1, ws1TotalCols, {
            font: { bold: true },
            fill: fillGrey('FFEFEFEF'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        if (ws1LastRow >= 5) {
            styleRange(ws1, 5, ws1LastRow, 1, ws1TotalCols, {
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });
            styleRange(ws1, 5, ws1LastRow, 2, 3, {
                border: thinBorder,
                alignment: { horizontal: 'left', vertical: 'middle' }
            });
            styleRange(ws1, 3, ws1LastRow, overallStartCol, ws1TotalCols, {
                fill: fillGrey('FFF5F5F5'),
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });
        }

        // ==================== SHEET 2: COMBINED STATS ====================
        const ws2 = workbook.addWorksheet(`Term ${currentTerm} Statistics`);

        const analysisLabel = activeSections.map(s => s.section_name).join(', ');
        ws2.addRow(['STATISTIC AND DIAGNOSTIC ANALYSIS OF MARKS']);
        ws2.addRow([]);
        ws2.addRow(['SUBJECT:', subjectname, '', '', 'MARK:', totalMaxMark]);
        ws2.addRow(['EXAMINER:', '', '', '', 'GRADE:', subjectGrade]);
        ws2.addRow(['DATE:', '', '', '', 'TERM:', currentTerm]);
        ws2.addRow(['ANALYSIS:', analysisLabel]);
        ws2.addRow([]);

        ws2.addRow([
            'Section', 'Total in class', 'Total written', 'Class average',
            'Highest mark', 'Lowest mark', 'Total failed', '% Failures',
            'Total that passed', '% Passed',
            '0 - 29', '30 - 39', '40 - 49', '50 - 59', '60 - 69', '70 - 79', '80 - 100',
            'Total (1-7 = Total/Total written)'
        ]);

        const sectionsByCategory = {};
        const categoryOrder = [];
        activeSections.forEach(section => {
            const category = (section.category || '').trim() || 'Uncategorized';
            if (!sectionsByCategory[category]) {
                sectionsByCategory[category] = [];
                categoryOrder.push(category);
            }
            sectionsByCategory[category].push(section);
        });

        const categoryTotalRows = [];

        categoryOrder.forEach(category => {
            const groupSections = sectionsByCategory[category];

            groupSections.forEach(section => {
                const stats = secStats[section.id];
                const max = parseFloat(section.max_mark) || 100;
                const tw = stats.marks.length;
                const avgPerc = tw > 0
                    ? stats.marks.reduce((a, b) => a + (b / max) * 100, 0) / tw
                    : null;
                const highest = tw > 0 ? Math.max(...stats.marks) : '';
                const lowest = tw > 0 ? Math.min(...stats.marks) : '';
                const total17 = Object.values(stats.levelDist).reduce((a, b) => a + b, 0);
                const failP = tw > 0 ? Math.round((stats.failedCount / tw) * 100) + '%' : '0%';
                const passP = tw > 0 ? Math.round((stats.passedCount / tw) * 100) + '%' : '0%';

                ws2.addRow([
                    `${section.section_name} (${max})`,
                    students.length,
                    tw,
                    avgPerc !== null ? avgPerc.toFixed(1) + '%' : '',
                    highest,
                    lowest,
                    stats.failedCount,
                    failP,
                    stats.passedCount,
                    passP,
                    stats.levelDist[1], stats.levelDist[2], stats.levelDist[3],
                    stats.levelDist[4], stats.levelDist[5], stats.levelDist[6], stats.levelDist[7],
                    tw > 0 ? `${total17}/${tw}` : ''
                ]);
            });

            const mappedName = category === 'Uncategorized' ? 'Uncategorized' : category;
            const currentCatStats = catStats[mappedName] || {
                percentages: [],
                levelDist: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 },
                passedCount: 0,
                failedCount: 0
            };
            const categoryTotalWritten = currentCatStats.percentages.length;
            const categoryAvgPerc = categoryTotalWritten > 0
                ? currentCatStats.percentages.reduce((a, b) => a + b, 0) / categoryTotalWritten
                : null;
            const categoryHighestPerc = categoryTotalWritten > 0 ? Math.round(Math.max(...currentCatStats.percentages)) + '%' : '';
            const categoryLowestPerc = categoryTotalWritten > 0 ? Math.round(Math.min(...currentCatStats.percentages)) + '%' : '';
            const categoryTotal17 = Object.values(currentCatStats.levelDist).reduce((a, b) => a + b, 0);
            const categoryFailP = categoryTotalWritten > 0 ? Math.round((currentCatStats.failedCount / categoryTotalWritten) * 100) + '%' : '0%';
            const categoryPassP = categoryTotalWritten > 0 ? Math.round((currentCatStats.passedCount / categoryTotalWritten) * 100) + '%' : '0%';

            ws2.addRow([
                `TOTAL ${category.toUpperCase()}`,
                students.length,
                categoryTotalWritten,
                categoryAvgPerc !== null ? categoryAvgPerc.toFixed(1) + '%' : '',
                categoryHighestPerc,
                categoryLowestPerc,
                currentCatStats.failedCount,
                categoryFailP,
                currentCatStats.passedCount,
                categoryPassP,
                currentCatStats.levelDist[1], currentCatStats.levelDist[2], currentCatStats.levelDist[3],
                currentCatStats.levelDist[4], currentCatStats.levelDist[5], currentCatStats.levelDist[6], currentCatStats.levelDist[7],
                categoryTotalWritten > 0 ? `${categoryTotal17}/${categoryTotalWritten}` : ''
            ]);
            categoryTotalRows.push(ws2.rowCount);
        });

        const totalWrittenOverall = overallRawData.length;
        const overallClassAvgPerc = totalWrittenOverall > 0
            ? overallRawData.reduce((a, b) => a + b.perc, 0) / totalWrittenOverall
            : null;
        const allRawSums = overallRawData.map(r => r.markSum);
        const highestOverall = allRawSums.length > 0 ? Math.max(...allRawSums) : '';
        const lowestOverall = allRawSums.length > 0 ? Math.min(...allRawSums) : '';
        const overallTotal17 = Object.values(overallLevelDist).reduce((a, b) => a + b, 0);
        const overallFailP = totalWrittenOverall > 0 ? Math.round((overallFailedCount / totalWrittenOverall) * 100) + '%' : '0%';
        const overallPassP = totalWrittenOverall > 0 ? Math.round((overallPassedCount / totalWrittenOverall) * 100) + '%' : '0%';

        ws2.addRow([
            `OVERALL TOTAL (${totalMaxMark})`,
            students.length,
            totalWrittenOverall,
            overallClassAvgPerc !== null ? overallClassAvgPerc.toFixed(1) + '%' : '',
            highestOverall,
            lowestOverall,
            overallFailedCount,
            overallFailP,
            overallPassedCount,
            overallPassP,
            overallLevelDist[1], overallLevelDist[2], overallLevelDist[3],
            overallLevelDist[4], overallLevelDist[5], overallLevelDist[6], overallLevelDist[7],
            totalWrittenOverall > 0 ? `${overallTotal17}/${totalWrittenOverall}` : ''
        ]);
        const overallTotalRow = ws2.rowCount;

        ws2.addRow([]);
        ws2.addRow(['DIAGNOSTIC ANALYSIS: (High frequency errors that were made in questions)']);
        ws2.addRow([]);
        ws2.addRow(['Problem area', '', '', '', 'Intervention', '', '', '', '', '', 'Time', '']);
        for (let i = 0; i < 4; i++) {
            ws2.addRow(['', '', '', '', '', '', '', '', '', '', '', '']);
        }

        ws2.addRow([]);
        ws2.addRow(['Signature of Teacher:', '', '', '', '', '', '', '', '', 'Date:', '', '', '', '']);
        ws2.addRow(['Signature of Departmental Head:', '', '', '', '', '', '', '', '', 'Date:', '', '', '', '']);

        ws2.mergeCells(1, 1, 1, 18);
        ws2.mergeCells(6, 2, 6, 18);
        ws2.columns = [
            { width: 24 }, { width: 12 }, { width: 12 }, { width: 13 }, { width: 12 }, { width: 12 },
            { width: 11 }, { width: 11 }, { width: 13 }, { width: 10 },
            { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 }, { width: 8 },
            { width: 18 }
        ];

        const tableHeaderRow = 8;
        const tableFirstDataRow = 9;
        const tableLastDataRow = overallTotalRow;
        const diagTitleRow = tableLastDataRow + 2;
        const diagHeaderRow = tableLastDataRow + 4;
        const diagLastRow = diagHeaderRow + 4;
        const sigRow1 = diagLastRow + 2;
        const sigRow2 = diagLastRow + 3;

        // Diagnostic section merges
        ws2.mergeCells(diagTitleRow, 1, diagTitleRow, 18);
        ws2.mergeCells(diagHeaderRow, 1, diagHeaderRow, 4);
        ws2.mergeCells(diagHeaderRow, 5, diagHeaderRow, 10);
        ws2.mergeCells(diagHeaderRow, 11, diagHeaderRow, 12);
        for (let di = 1; di <= 4; di++) {
            ws2.mergeCells(diagHeaderRow + di, 1, diagHeaderRow + di, 4);
            ws2.mergeCells(diagHeaderRow + di, 5, diagHeaderRow + di, 10);
            ws2.mergeCells(diagHeaderRow + di, 11, diagHeaderRow + di, 12);
            ws2.getRow(diagHeaderRow + di).height = 45;
        }

        // Signature row merges
        [sigRow1, sigRow2].forEach(r => {
            ws2.mergeCells(r, 1, r, 2);
            ws2.mergeCells(r, 3, r, 8);
            ws2.mergeCells(r, 11, r, 14);
        });

        styleRange(ws2, 1, 1, 1, 18, {
            font: { bold: true },
            fill: fillGrey('FFD9D9D9'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        styleRange(ws2, 3, 6, 1, 1, {
            font: { bold: true },
            fill: fillGrey('FFEFEFEF'),
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'middle' }
        });
        styleRange(ws2, 3, 6, 2, 18, {
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'middle' }
        });

        styleRange(ws2, tableHeaderRow, tableHeaderRow, 1, 18, {
            font: { bold: true },
            fill: fillGrey('FFE5E5E5'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        if (tableLastDataRow >= tableFirstDataRow) {
            styleRange(ws2, tableFirstDataRow, tableLastDataRow, 1, 18, {
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });
            styleRange(ws2, tableFirstDataRow, tableLastDataRow, 1, 1, {
                border: thinBorder,
                alignment: { horizontal: 'left', vertical: 'middle' }
            });
        }

        categoryTotalRows.forEach(r => {
            styleRange(ws2, r, r, 1, 18, {
                font: { bold: true },
                fill: fillGrey('FFF9F9F9'),
                border: thinBorder,
                alignment: { horizontal: 'center', vertical: 'middle' }
            });
            styleRange(ws2, r, r, 1, 1, {
                font: { bold: true },
                fill: fillGrey('FFF9F9F9'),
                border: thinBorder,
                alignment: { horizontal: 'left', vertical: 'middle' }
            });
        });

        styleRange(ws2, overallTotalRow, overallTotalRow, 1, 18, {
            font: { bold: true },
            fill: fillGrey('FFF5F5F5'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        styleRange(ws2, diagTitleRow, diagTitleRow, 1, 18, {
            font: { bold: true },
            fill: fillGrey('FFEFEFEF'),
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'middle' }
        });

        styleRange(ws2, diagHeaderRow, diagHeaderRow, 1, 12, {
            font: { bold: true },
            fill: fillGrey('FFE5E5E5'),
            border: thinBorder,
            alignment: { horizontal: 'center', vertical: 'middle' }
        });

        styleRange(ws2, diagHeaderRow + 1, diagLastRow, 1, 12, {
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'top' }
        });

        // Signature rows styling
        [sigRow1, sigRow2].forEach(r => {
            ws2.getRow(r).height = 32;
            ws2.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'bottom' };
            ws2.getCell(r, 3).border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
            ws2.getCell(r, 10).font = { bold: false };
            ws2.getCell(r, 10).alignment = { horizontal: 'left', vertical: 'bottom' };
            ws2.getCell(r, 11).border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
        });

        const filename = `${(subjectname || 'Subject').replace(/\s+/g, '_')}_${(subjectClass || 'Class').replace(/\s+/g, '_')}_Term_${currentTerm}_Report.xlsx`;
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
    }, [studentsWithComputed, sections, sectionMarks, calculateLevel, formatGrade, currentTerm, subjectname, subjectClass, subjectGrade, students.length, foundationSubjectDefinition, foundationTemplateCode, isFoundationSubject]);

    const handleDownloadTemplate = async () => {
        if (!sections.length && !students.length) { alert('Add sections or students first.'); return; }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Template');
        const usesGroupedHeaders = isFoundationSubject || hasGroupedColumnHeaders;

        if (usesGroupedHeaders) {
            worksheet.addRow([
                'First Name',
                'Last Name',
                ...sections.map((section) => normalizeGroupText(sectionHeaderMeta[section.id]?.groupLabel || getAssessmentGroupLabel(section)))
            ]);
            worksheet.addRow([
                '',
                '',
                ...sections.map((section) => normalizeGroupText(section.section_name || 'Assessment'))
            ]);
            worksheet.mergeCells(1, 1, 2, 1);
            worksheet.mergeCells(1, 2, 2, 2);
        } else {
            worksheet.addRow(['First Name', 'Last Name', ...sections.map(s => s.section_name)]);
        }

        students.forEach(s => {
            const row = [s.first_name, s.last_name];
            sections.forEach(() => row.push(''));
            worksheet.addRow(row);
        });

        const headerRows = usesGroupedHeaders ? [worksheet.getRow(1), worksheet.getRow(2)] : [worksheet.getRow(1)];
        headerRows.forEach((headerRow, rowIndex) => {
            headerRow.font = { bold: true };
            headerRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            headerRow.height = rowIndex === 0 && usesGroupedHeaders ? 24 : 22;
            headerRow.eachCell(cell => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: rowIndex === 0 ? 'FFE8F1EA' : 'FFF7FAF7' }
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
                    left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
                    bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
                    right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
                };
            });
        });

        worksheet.views = [{ state: 'frozen', ySplit: usesGroupedHeaders ? 2 : 1 }];

        worksheet.columns = [
            { width: 18 },
            { width: 18 },
            ...sections.map((section) => ({
                width: normalizeGroupText(sectionHeaderMeta[section.id]?.groupLabel || '').length > 0 ? 18 : 14
            }))
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        saveAs(blob, `Template_${(subjectname || 'Subject').replace(/\s+/g, '_')}_Term${currentTerm}.xlsx`);
    };

    // --- Section Management Handlers ---
    const handleAddSection = async (e) => {
        e.preventDefault();
        if (!newSection.section_name.trim()) return alert('Name required');
        setAddingSection(true);
        try {
            const normalizedGroup = normalizeGroupText(newSection.assessment_group);
            const matchingGroup = matchingAssessmentGroup;
            await axios.post(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                subject_id: id,
                term: currentTerm,
                ...newSection,
                weight: matchingGroup ? matchingGroup.currentWeight : parsePositiveNumber(newSection.weight, 1),
                assessment_group: normalizedGroup
            });
            setNewSection((prev) => ({
                ...prev,
                section_name: '',
                max_mark: 100,
                weight: matchingGroup ? matchingGroup.currentWeight : parsePositiveNumber(prev.weight, 1),
                assessment_group: normalizedGroup
            }));
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`);
            setSections(res.data.sections || []);
        } catch (e) { alert('Failed'); }
        finally { setAddingSection(false); }
    };

    const handleUpdateSection = (id, field, val) => {
        if (field === 'max_mark') setSectionMaxMarks(p => ({ ...p, [id]: val }));

        const timerRef = maxMarkTimersRef;
        if (timerRef.current[id]) clearTimeout(timerRef.current[id]);

        timerRef.current[id] = setTimeout(async () => {
            try {
                const section = sections.find(s => s.id === id);
                if (!section) return;

                await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                    id,
                    section_name: section.section_name,
                    weight: section.weight,
                    max_mark: field === 'max_mark' ? val : section.max_mark,
                    category: section.category || '',
                    assessment_group: section.assessment_group || ''
                });

                setSections(prev => prev.map(s =>
                    s.id === id ? { ...s, [field]: parseFloat(val) } : s
                ));
                if (field === 'max_mark') {
                    setSectionMaxMarks(prev => { const u = { ...prev }; delete u[id]; return u; });
                }

            } catch (e) {
                alert('Update failed. Check your connection.');
            }
        }, 1500);
    };

    const SECTION_CATEGORIES = ['', 'Test', 'Assignment','Investigation','Project','Exam','Practical','Oral','Transactional Writing','Essay','SBA','Other'];

    const handleUpdateSectionCategory = async (sectionId, val) => {
        try {
            const section = sections.find(s => s.id === sectionId);
            if (!section) return;
            await axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                id: sectionId,
                section_name: section.section_name,
                weight: section.weight,
                max_mark: section.max_mark,
                category: val,
                assessment_group: section.assessment_group || ''
            });
            setSections(prev => prev.map(s => s.id === sectionId ? { ...s, category: val } : s));
        } catch (e) {
            alert('Update failed. Check your connection.');
        }
    };

    const handleUpdateAssessmentGroupName = async (group) => {
        const nextGroup = normalizeGroupText(assessmentNameDrafts[group.key] ?? group.primaryGroupName);
        const currentGroupName = normalizeGroupText(group.primaryGroupName);

        if (nextGroup === currentGroupName) {
            setAssessmentNameDrafts(prev => {
                const updated = { ...prev };
                delete updated[group.key];
                return updated;
            });
            return;
        }

        setSavingAssessmentNameKey(group.key);
        try {
            await Promise.all(group.sections.map((section) => axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                id: section.id,
                section_name: section.section_name,
                weight: section.weight,
                max_mark: section.max_mark,
                category: section.category || '',
                assessment_group: nextGroup
            })));

            const sectionIds = new Set(group.sections.map((section) => section.id));
            setSections(prev => prev.map((section) => (
                sectionIds.has(section.id) ? { ...section, assessment_group: nextGroup } : section
            )));
            setAssessmentNameDrafts(prev => {
                const updated = { ...prev };
                delete updated[group.key];
                return updated;
            });
        } catch (e) {
            alert('Assessment name update failed. Check your connection.');
        } finally {
            setSavingAssessmentNameKey('');
        }
    };

    const handleUpdateAssessmentGroupWeight = async (group) => {
        const draftValue = assessmentWeightDrafts[group.key];
        const nextWeight = parsePositiveNumber(draftValue, group.currentWeight);
        const currentWeight = parsePositiveNumber(group.currentWeight, 1);

        if (Math.abs(nextWeight - currentWeight) < 0.0001 && !group.hasMixedWeights) {
            setAssessmentWeightDrafts(prev => {
                const updated = { ...prev };
                delete updated[group.key];
                return updated;
            });
            return;
        }

        setSavingAssessmentGroupKey(group.key);
        try {
            await Promise.all(group.sections.map((section) => axios.put(`${process.env.REACT_APP_API_URL}/task_sections.php`, {
                id: section.id,
                section_name: section.section_name,
                weight: nextWeight,
                max_mark: section.max_mark,
                category: section.category || '',
                assessment_group: section.assessment_group || ''
            })));

            const sectionIds = new Set(group.sections.map((section) => section.id));
            setSections(prev => prev.map((section) => (
                sectionIds.has(section.id) ? { ...section, weight: nextWeight } : section
            )));
            setAssessmentWeightDrafts(prev => {
                const updated = { ...prev };
                delete updated[group.key];
                return updated;
            });
        } catch (e) {
            alert('Weight update failed. Check your connection.');
        } finally {
            setSavingAssessmentGroupKey('');
        }
    };

    const handleDeleteSection = async (sid, name) => {
        if (!window.confirm(`Delete ${name}?`)) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/task_sections.php?id=${sid}`);
            setSections(prev => prev.filter(s => s.id !== sid));
        } catch (e) { alert('Error deleting'); }
    };

    // --- Mark Handlers (Optimized) ---
    const handleSectionMarkChange = useCallback((studentId, sectionId, value) => {
        const key = `${studentId}-${sectionId}`;
        let sanitized = value.replace(/[^0-9.]/g, '');

        // Allow empty string (for deletion)
        if (sanitized === '') {
            setMarksBeingEdited(prev => ({ ...prev, [key]: '' }));
            setSectionMarks(prev => {
                const idx = prev.findIndex(m => parseInt(m.student_id) === parseInt(studentId) && parseInt(m.section_id) === parseInt(sectionId));
                if (idx !== -1) {
                    const newMarks = [...prev];
                    newMarks[idx] = { ...newMarks[idx], mark: '' };
                    return newMarks;
                }
                return prev;
            });
            return;
        }

        // Validate and cap the value
        if (!isNaN(sanitized)) {
            let num = parseFloat(sanitized);
            const section = sections.find(s => s.id === sectionId);
            const max = section?.max_mark || 100;
            if (num > max) num = max;
            sanitized = String(num);
        }

        setMarksBeingEdited(prev => ({ ...prev, [key]: sanitized }));

        setSectionMarks(prev => {
            const idx = prev.findIndex(m => parseInt(m.student_id) === parseInt(studentId) && parseInt(m.section_id) === parseInt(sectionId));
            if (idx !== -1 && prev[idx].mark === sanitized) return prev; // Prevent unnecessary updates

            const newMarks = [...prev];
            if (idx !== -1) {
                newMarks[idx] = { ...newMarks[idx], mark: sanitized };
            } else {
                newMarks.push({ student_id: parseInt(studentId), section_id: parseInt(sectionId), mark: sanitized });
            }
            return newMarks;
        });
    }, [sections]);

    const handleMarkBlur = useCallback(async (studentId, sectionId) => {
        const key = `${studentId}-${sectionId}`;
        const val = marksBeingEdited[key];
        if (val !== undefined) {
            try {
                // Find the existing mark record
                const markObj = sectionMarks.find(m =>
                    parseInt(m.student_id) === parseInt(studentId) &&
                    parseInt(m.section_id) === parseInt(sectionId)
                );

                if (val === '' || val === null) {
                    // Empty value - DELETE the mark if it exists
                    if (markObj && markObj.id) {
                        await axios.delete(`${process.env.REACT_APP_API_URL}/section_marks.php?id=${markObj.id}`);
                        // Remove from state
                        setSectionMarks(prev => prev.filter(m => m.id !== markObj.id));
                    }
                } else {
                    // Non-empty value - POST or PUT
                    if (markObj && markObj.id) {
                        // Update existing mark
                        await axios.put(`${process.env.REACT_APP_API_URL}/section_marks.php`, {
                            id: markObj.id,
                            mark: val
                        });
                    } else {
                        // Create new mark
                        const response = await axios.post(`${process.env.REACT_APP_API_URL}/section_marks.php`, {
                            student_id: studentId,
                            section_id: sectionId,
                            mark: val
                        });
                        // Update the mark in state with the new ID from response
                        if (response.data.id) {
                            setSectionMarks(prev => {
                                const idx = prev.findIndex(m =>
                                    parseInt(m.student_id) === parseInt(studentId) &&
                                    parseInt(m.section_id) === parseInt(sectionId)
                                );
                                if (idx !== -1) {
                                    const newMarks = [...prev];
                                    newMarks[idx] = { ...newMarks[idx], id: response.data.id };
                                    return newMarks;
                                }
                                return prev;
                            });
                        }
                    }
                }

                setMarksBeingEdited(prev => {
                    const u = { ...prev };
                    delete u[key];
                    return u;
                });
            } catch (e) {
                console.error('Mark save error:', e);
                alert('Error saving mark: ' + (e.response?.data?.error || e.message));
            }
        }
    }, [marksBeingEdited, sectionMarks]);

    // --- Student Handlers ---
    const handleAddStudent = async () => {
        if (!newStudent.firstName) return alert('Name required');
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/add_student_with_marks.php`, {
                subject_id: id, first_name: newStudent.firstName, last_name: newStudent.lastName
            });
            if (res.data.success) {
                const newStudentData = {
                    student_id: res.data.student_id,
                    first_name: newStudent.firstName,
                    last_name: newStudent.lastName,
                    level: null,
                    average: null
                };
                setStudents(prev => [...prev, newStudentData]);
                setNewStudent({ firstName: '', lastName: '' });
                alert('Student added successfully!');
            } else {
                alert('Failed to add student');
            }
        } catch (e) { alert('Error adding student: ' + e.message); }
    };

    const handleDeleteStudent = async (sid, name) => {
        if (!window.confirm(`Remove student ${name || ''}?`)) return;
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/delete_student.php`, { student_id: sid, subject_id: id });
            setStudents(prev => prev.filter(s => s.student_id !== sid));
        } catch (e) { alert('Error'); }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm('Delete ALL DATA?')) return;
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/delete_all_students.php`, { subject_id: id });
            setStudents([]);
            setSectionMarks([]);
            alert('All students and marks deleted successfully!');
        } catch (e) { alert('Error deleting: ' + e.message); }
    };

    const loadSubjectData = useCallback(async ({ showLoader = true } = {}) => {
        if (showLoader) {
            setLoading(true);
        }

        try {
            const [classRes, secRes, marksRes, notesRes] = await Promise.allSettled([
                axios.get(`${process.env.REACT_APP_API_URL}/fetch_class_marks.php?subject_id=${id}&term=${currentTerm}`),
                axios.get(`${process.env.REACT_APP_API_URL}/task_sections.php?subject_id=${id}&term=${currentTerm}`),
                axios.get(`${process.env.REACT_APP_API_URL}/section_marks.php?subject_id=${id}&term=${currentTerm}`),
                axios.get(`${process.env.REACT_APP_API_URL}/subject_notes.php?subject_id=${id}`)
            ]);

            // Set students data
            const classData = classRes.status === 'fulfilled' ? classRes.value.data : {};
            const studentsData = (classData.students || []).map(s => ({
                ...s,
                level: s.average ? calculateLevel(s.average) : null
            }));
            setStudents(studentsData);
            setSubjectName(classData.subject_name || 'Subject');
            setSubjectGrade(classData.grade || '');
            setSubjectClass(classData.class_name || '');
            setSubjectGradingMode(classData.grading_mode || 'standard');
            setFoundationTemplateCode(classData.foundation_template_code || '');
            setFoundationTemplateDefinition(classData.foundation_template || null);

            // Set sections and max marks
            const secData = secRes.status === 'fulfilled' ? secRes.value.data : { sections: [] };
            const sectionsData = secData.sections || [];
            setSections(sectionsData);

            const maxes = {};
            sectionsData.forEach(s => {
                maxes[s.id] = s.max_mark;
            });
            setSectionMaxMarks(maxes);

            // Set marks
            const marksData = marksRes.status === 'fulfilled' ? marksRes.value.data : { marks: [] };
            setSectionMarks(marksData.marks || []);

            // Set notes (gracefully handle failure)
            const notesData = notesRes.status === 'fulfilled' ? notesRes.value.data : { notes: [] };
            setNotes(notesData.notes || []);
            setMarksBeingEdited({});
        } catch (e) {
            console.error('Load error:', e);
        } finally {
            if (showLoader) {
                setLoading(false);
            }
        }
    }, [id, currentTerm, calculateLevel]);

    useEffect(() => {
        const storedTerm = getStoredSubjectTerm(id);
        setCurrentTerm(storedTerm);
        setTermReadySubjectId(id || '');
        setMarksBeingEdited({});
    }, [id]);

    useEffect(() => {
        if (typeof window === 'undefined' || !id || termReadySubjectId !== id) {
            return;
        }

        window.localStorage.setItem(getSubjectTermStorageKey(id), String(currentTerm));
    }, [id, currentTerm, termReadySubjectId]);

    // --- Initial Load (Optimized) ---
    useEffect(() => {
        if (!id || termReadySubjectId !== id) {
            return;
        }

        const init = async () => {
            await loadSubjectData();
        };
        init();
    }, [id, termReadySubjectId, loadSubjectData]);

    return (
        <div className="subject-detail-wrapper">
            <style>{`
                :root {
                    --primary-green: #4CAF50;
                    --dark-green: #388E3C;
                    --light-green: #E8F5E9;
                    --text-dark: #2c3e50;
                    --text-light: #7f8c8d;
                    --border-color: #ecf0f1;
                    --card-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
                }

                body { background-color: #f8f9fa; font-family: 'Inter', -apple-system, sans-serif; color: var(--text-dark); }
                
                 .subject-detail-wrapper {
                     padding: calc(2rem + var(--shell-top-offset)) 2rem 2rem;
                     margin-left: 220px; 
                     max-width: 1600px;
                 }

                .page-header {
                  
                    padding: 1.75rem 2rem;
                    border-radius: 12px;
                    box-shadow: var(--card-shadow);
                    display: grid;
                    grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr);
                    gap: 1.5rem;
                    align-items: start;
                    margin-bottom: 2rem;
                }
                .subject-hero { display: grid; gap: 0.8rem; }
                .page-kicker {
                    margin: 0;
                    font-size: 0.76rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.14em;
                    color: #2f7a4b;
                }
                .page-header h1 { margin: 0; font-size: 1.5rem; font-weight: 700; color: var(--text-dark); }
                .page-summary { margin: 0; color: #5f6f66; line-height: 1.5; max-width: 720px; }
                .header-meta { display: flex; flex-wrap: wrap; gap: 0.65rem; }
                .meta-chip, .badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.35rem;
                    background: rgba(232, 245, 233, 0.95);
                    color: var(--dark-green);
                    padding: 0.4rem 0.85rem;
                    border-radius: 999px;
                    font-size: 0.82rem;
                    font-weight: 600;
                    border: 1px solid rgba(76, 175, 80, 0.14);
                }
                .badge { margin-left: 10px; }
                .header-side { display: grid; gap: 1rem; justify-items: end; }
                .page-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 0.75rem; }
                .workspace-summary {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }
                .summary-card {
                    background: white;
                    border: 1px solid var(--border-color);
                    border-radius: 14px;
                    padding: 1rem 1.1rem;
                    box-shadow: var(--card-shadow);
                }
                .summary-label {
                    margin: 0 0 0.35rem;
                    color: var(--text-light);
                    font-size: 0.78rem;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    font-weight: 700;
                }
                .summary-value {
                    margin: 0 0 0.2rem;
                    font-size: 1.55rem;
                    line-height: 1;
                    color: var(--text-dark);
                    font-weight: 800;
                }
                .summary-note { margin: 0; color: #66756c; font-size: 0.88rem; }
                .section-block { margin-bottom: 1.75rem; }
                .section-heading {
                    display: flex;
                    justify-content: space-between;
                    align-items: end;
                    gap: 1rem;
                    margin-bottom: 1rem;
                    flex-wrap: wrap;
                }
                .section-heading h2 {
                    margin: 0;
                    color: var(--text-dark);
                    font-size: 1.28rem;
                    font-weight: 700;
                }
                .section-kicker {
                    margin: 0 0 0.3rem;
                    color: #2f7a4b;
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.12em;
                }
                .section-copy { margin: 0.35rem 0 0; color: #66756c; line-height: 1.5; max-width: 760px; }
                .section-actions { display: flex; gap: 0.75rem; flex-wrap: wrap; }
                .helper-text { margin: 0; color: #738379; font-size: 0.83rem; line-height: 1.45; }

                .term-tabs {
                    display: flex;
                    gap: 10px;
                    background: #f1f3f5;
                    padding: 5px;
                    border-radius: 25px;
                }
                .term-tab {
                    padding: 8px 24px;
                    border: none;
                    border-radius: 20px;
                    font-weight: 600;
                    color: #7f8c8d;
                    background: transparent;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 0.9rem;
                }
                .term-tab.active {
                    background: white;
                    color: var(--primary-green);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }
                .term-tab:hover:not(.active) {
                    color: var(--text-dark);
                }

                .management-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 1.5rem;
                }
                .modern-card { background: white; border-radius: 12px; padding: 1.5rem; box-shadow: var(--card-shadow); border: 1px solid var(--border-color); }
                .card-title { font-size: 1.1rem; font-weight: 600; margin-top: 0; margin-bottom: 1rem; color: var(--text-dark); border-bottom: 2px solid var(--light-green); padding-bottom: 0.5rem; display: inline-block; }
                .card-subtitle { margin: 0.75rem 0 0; color: #66756c; font-size: 0.92rem; line-height: 1.45; }
                .setup-card { order: -1; }
                .assessment-guidance {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.65rem;
                    margin: 1rem 0;
                }
                .guidance-pill {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.45rem 0.75rem;
                    border-radius: 999px;
                    background: #eef5ef;
                    color: #315f42;
                    font-size: 0.82rem;
                    font-weight: 600;
                    border: 1px solid #d7e5d8;
                }
                .form-banner {
                    margin-bottom: 1rem;
                    padding: 0.9rem 1rem;
                    border-radius: 10px;
                    background: #f6fbf6;
                    border: 1px solid #dce9dd;
                    color: #456150;
                    font-size: 0.92rem;
                    line-height: 1.45;
                }
                .section-form-grid {
                    display: grid;
                    grid-template-columns: repeat(12, minmax(0, 1fr));
                    gap: 0.9rem;
                    align-items: end;
                    background: #f8f9fa;
                    padding: 1rem;
                    border-radius: 12px;
                    margin-bottom: 1rem;
                }
                .stacked-field {
                    display: grid;
                    gap: 0.4rem;
                    min-width: 0;
                }
                .stacked-field label,
                .stacked-field span {
                    margin: 0;
                    font-size: 0.76rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    color: #5a6a61;
                }
                .section-form-grid .stacked-field:nth-child(1) { grid-column: span 4; }
                .section-form-grid .stacked-field:nth-child(2) { grid-column: span 4; }
                .section-form-grid .stacked-field:nth-child(3) { grid-column: span 1; }
                .section-form-grid .stacked-field:nth-child(4) { grid-column: span 1; }
                .section-form-grid .stacked-field:nth-child(5) { grid-column: span 2; }
                .section-form-grid .btn { grid-column: span 12; width: 100%; }
                .assessment-group-list { display: grid; gap: 1rem; }
                .assessment-group-header {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    justify-content: space-between;
                    align-items: start;
                }
                .assessment-group-title {
                    display: grid;
                    gap: 0.45rem;
                    flex: 1 1 220px;
                    min-width: 0;
                }
                .assessment-group-title h4 {
                    margin: 0;
                    font-size: 1.05rem;
                    color: var(--text-dark);
                }
                .assessment-group-meta {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }
                .assessment-chip {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.35rem 0.65rem;
                    border-radius: 999px;
                    background: #ffffff;
                    border: 1px solid #dde7de;
                    color: #53635b;
                    font-size: 0.8rem;
                    font-weight: 600;
                }
            
                .section-row-list { display: grid; gap: 0.75rem; }
                .section-row {
                    display: grid;
                    grid-template-columns: minmax(180px, 1.4fr) minmax(110px, 0.75fr) minmax(150px, 1fr) auto;
                    gap: 0.85rem;
                    align-items: end;
                    padding: 0.9rem;
                    border-radius: 12px;
                    background: #ffffff;
                    border: 1px solid #e8eeea;
                }
             
                .section-row-subtext {
                    margin: 0.25rem 0 0;
                    color: #78867f;
                    font-size: 0.84rem;
                    line-height: 1.4;
                }
                .section-row .btn-danger {
                    align-self: center;
                    justify-self: end;
                }
                .modern-input { width: 100%; padding: 0.6rem; border: 1px solid #dfe6e9; border-radius: 6px; font-size: 0.9rem; box-sizing: border-box; }
                .compact-notes-card { min-width: 0; box-sizing: border-box; }
                .compact-notes-card .compact-notes-form { display: flex; flex-direction: column; gap: 8px; }
                .compact-notes-card .compact-notes-form > .modern-input { margin-bottom: 4px; }
                .compact-notes-card .compact-datetime { padding: 8px 10px; border: 1px solid #e0e0e0; border-radius: 6px; font-size: 0.9rem; }
                .compact-notes-card .compact-add-btn { width: 120px; flex: none; }
                .compact-notes-card .compact-notes-form, .compact-notes-card .compact-notes-form * { min-width: 0; box-sizing: border-box; }
                .compact-notes-card input[type="datetime-local"] { min-width: 0; flex: 1 1 auto; }
                .modern-input:focus { outline: none; border-color: var(--primary-green); box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1); }
                .btn { padding: 0.6rem 1.2rem; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s; font-size: 0.9rem; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
                .btn-primary { background: var(--primary-green); color: white; width: 100%; }
                .btn-outline { background:var(--primary-green); border: 1px solid var(--primary-green); color: white; width: 100%; }
                .btn-danger { background: #fee2e2; color: #dc2626; width: auto; padding: 0.4rem 0.8rem; font-size: 0.8rem; }
                
                .table-container { background: white; border-radius: 12px; box-shadow: var(--card-shadow); overflow-x: scroll; overflow-y: auto; border: 1px solid var(--border-color); margin-bottom: 30px; padding: 1rem; }
                .table-toolbar {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    justify-content: space-between;
                    align-items: center;
                    padding-bottom: 1rem;
                    margin-bottom: 1rem;
                    border-bottom: 1px solid #edf1ef;
                }
                .table-toolbar-copy { display: grid; gap: 0.25rem; }
                .table-toolbar-title { font-weight: 700; color: #55655d; }
                .table-toolbar-note { font-size: 0.88rem; color: #78867f; line-height: 1.45; }
                .modern-table { width: 100%; border-collapse: collapse; min-width: 800px; }
                .modern-table th { background: #f8f9fa; padding: 1rem; text-align: center; font-weight: 600; color: var(--text-dark); border-bottom: 2px solid var(--border-color); white-space: normal; position: sticky; top: 0; z-index: 1; }
                .modern-table td { padding: 0.5rem; border-bottom: 1px solid var(--border-color); text-align: center; color: var(--text-dark); }
                .cell-input { width: 100%; padding: 8px; border: 1px solid transparent; border-radius: 4px; text-align: center; font-weight: 500; background: transparent; }
                .cell-input:focus { background: white; border-color: var(--primary-green); outline: none; }
                
                .analytics-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
                .stat-card { background: white; border-radius: 12px; padding: 24px; box-shadow: var(--card-shadow); border: 1px solid var(--border-color); border-top: 4px solid var(--primary-green); display: flex; flex-direction: column; justify-content: space-between; min-height: 200px; }
                .stat-grid-inner { display: grid; grid-template-columns: 1fr 1fr; column-gap: 20px; row-gap: 16px; margin-bottom: 20px; }
                .stat-item { display: flex; flex-direction: column; }
                .stat-label { font-size: 0.75rem; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
                .stat-value { font-size: 1.25rem; font-weight: 700; color: var(--text-dark); }
                .stat-value.green { color: var(--primary-green); }
                .stat-value.red { color: #e74c3c; }
                .submission-count { margin-top: auto; padding-top: 15px; border-top: 1px dashed #eee; font-size: 0.85rem; color: var(--text-light); display: flex; align-items: center; gap: 8px; font-weight: 500; }
                
                .loading-overlay { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 300px; color: var(--text-light); }
                .spinner { width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid var(--primary-green); border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 1rem; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @media print { .no-print { display: none !important; } .subject-detail-wrapper { margin: 0; padding: 0; } }

                /* --- MOBILE RESPONSIVE FIXES --- */
                @media screen and (max-width: 768px) {
                     .subject-detail-wrapper {
                          margin-left: 0;
                          padding: var(--shell-top-offset-mobile) 10px 10px;
                          width: 100%;
                      }
                    .page-header {
                        grid-template-columns: 1fr;
                        gap: 1rem;
                        padding: 1rem;
                    }
                    .page-header h1 {
                        font-size: 1.2rem;
                        display: flex;
                        flex-direction: column;
                        gap: 5px;
                    }
                    .page-header > div { width: 100%; }
                    .badge { margin-left: 0; align-self: flex-start; }
                    .header-side { justify-items: stretch; }
                    .page-actions { justify-content: stretch; }
                    .page-actions > button { width: 100%; }
                    .term-tabs {
                        width: 100%;
                        overflow-x: auto;
                        padding-bottom: 5px;
                        justify-content: flex-start;
                    }
                    .term-tab {
                        white-space: nowrap;
                        flex-shrink: 0;
                        padding: 6px 16px;
                    }
                    .management-grid {
                        grid-template-columns: 1fr;
                    }
                    .section-form-grid {
                        grid-template-columns: 1fr;
                    }
                    .section-form-grid .stacked-field,
                    .section-form-grid .btn {
                        grid-column: auto;
                    }
                    .assessment-group-header,
                    .table-toolbar {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .section-row {
                        grid-template-columns: 1fr;
                    }
                   
                    .section-row .btn-danger {
                        justify-self: stretch;
                    }
                    .page-header button {
                        width: 100%;
                        margin-bottom: 5px;
                    }
                    form[style*="display: flex"] {
                        flex-direction: column;
                        align-items: stretch !important;
                    }
                    form[style*="display: flex"] input,
                    form[style*="display: flex"] button {
                        width: 100% !important;
                        flex: none !important;
                        margin-bottom: 5px;
                    }
                    .table-container {
                        width: 100%;
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        margin-bottom: 1rem;
                    }
                    .modern-table {
                        min-width: 600px;
                    }
                    .analytics-grid {
                        grid-template-columns: 1fr;
                    }
                    /* Ensure compact notes card is full width on mobile */
                    .compact-notes-card { width: 100%; }
                    .compact-notes-card .compact-notes-form { flex-direction: column; }
                    
                    .compact-notes-card .compact-notes-form, .compact-notes-card .compact-notes-form * { min-width: 0; }
                    .compact-notes-card input[type="datetime-local"] { -webkit-appearance: textfield; }
                    /* smaller breakpoint adjustments */
                    @media screen and (max-width: 480px) {
                        .compact-notes-card { padding: 12px; }
                        .compact-notes-card .compact-add-btn { padding: 10px 12px; }
                        .compact-notes-card .compact-datetime { font-size: 0.9rem; }
                    }
                }
            `}</style>

            {/* Header */}
            <div className="page-header">
                <div className="subject-hero">
                
                    <h1>{subjectname} <span className="badge">{subjectGrade} {subjectClass}</span></h1>
                   
                    <div className="header-meta">
                        <span className="meta-chip">Term {currentTerm}</span>
                        <span className="meta-chip">{students.length} Learner{students.length === 1 ? '' : 's'}</span>
                        <span className="meta-chip">{sections.length} Section{sections.length === 1 ? '' : 's'}</span>
                        <span className="meta-chip">{assessmentGroups.length} Assessment{assessmentGroups.length === 1 ? '' : 's'}</span>
                    </div>
                </div>

                <div className="header-side">
                    <div className="term-tabs no-print">
                        {[1, 2, 3, 4].map(term => (
                            <button
                                key={term}
                                className={`term-tab ${currentTerm === term ? 'active' : ''}`}
                                onClick={() => setCurrentTerm(term)}
                            >
                                Term {term}
                            </button>
                        ))}
                    </div>

                    <div className="page-actions">
                        <button className="btn btn-danger no-print" onClick={handleDeleteAll}>
                            <Icons.Trash /> Reset All Marks to 0
                        </button>
                    </div>
                </div>
            </div>

          
            <div className="section-block no-print">
               

            <div className="management-grid">

                {/* CARD 1: File Operations (Collapsible) */}
                <div className="modern-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 className="card-title" style={{ margin: 0 }}>Import / Export (Term {currentTerm})</h3>
                        <button
                            type="button"
                            className="btn btn-outline"
                            style={{ fontSize: '1.1rem', padding: '2px 10px', width: 40, height: '32px', marginLeft: 8 }}
                            onClick={() => setImportExportCollapsed(v => !v)}
                            aria-label={importExportCollapsed ? 'Expand Import/Export' : 'Collapse Import/Export'}
                        >
                            {importExportCollapsed ? '+' : '-'}
                        </button>
                    </div>
                    <p className="card-subtitle">Bring marks in from Excel, export marks and statistics.</p>
                    {!importExportCollapsed && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop:'10px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <button className="btn btn-outline" onClick={handleDownloadTemplate}>Download Excel Template</button>
                            
                            </div>
                            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px dashed #ced4da', textAlign: 'center' }}>
                                {csvUploading ? (
                                    <div style={{ color: 'var(--primary-green)', fontWeight: '600' }}>
                                        <div className="spinner" style={{ width: '20px', height: '20px', display: 'inline-block', verticalAlign: 'middle', marginRight: '8px', marginBottom: 0 }}></div>
                                        {csvProgress}
                                    </div>
                                ) : (
                                    <>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: '#666' }}>Upload Data for Term {currentTerm}</label>
                                        <input type="file" accept=".xlsx, .xls, .csv" onChange={handleExcelUpload} className="modern-input" />
                                        <a
                                            href="/assets/marksure-add-assessment-classes.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ display: 'inline-block', marginTop: '10px', fontSize: '0.85rem', color: 'var(--dark-green)', fontWeight: '600', textDecoration: 'none' }}
                                        >
                                            Need help? Open Excel import guide (PDF)
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* CARD 2: Add Student (Collapsible) */}
                <div className="modern-card add-student-card">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 className="card-title" style={{ margin: 0 }}>Add Student</h3>
                        <button
                            type="button"
                            className="btn btn-outline"
                            style={{ fontSize: '1.1rem', padding: '2px 10px', width: 40, height: '32px', marginLeft: 8 }}
                            onClick={() => setAddStudentCollapsed(v => !v)}
                            aria-label={addStudentCollapsed ? 'Expand Add Student' : 'Collapse Add Student'}
                        >
                            {addStudentCollapsed ? '+' : '-'}
                        </button>
                    </div>
                    <p className="card-subtitle">Add a learner straight into this subject when they join mid-term or were missed during import.</p>
                    {!addStudentCollapsed && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px',marginTop:'20px'
                         }}>
                            <input className="modern-input" placeholder="First Name" value={newStudent.firstName} onChange={e => setNewStudent({ ...newStudent, firstName: e.target.value })} />
                            <input className="modern-input" placeholder="Last Name" value={newStudent.lastName} onChange={e => setNewStudent({ ...newStudent, lastName: e.target.value })} />
                            <button className="btn btn-primary" onClick={handleAddStudent}>+ Add Student</button>
                        </div>
                    )}
                </div>

             

                {/* CARD 3: Manage Sections (Dynamic per Term) */}
              <div className="modern-card setup-card" style={{ gridColumn: '1 / -1' }}>

    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 className="card-title" style={{ margin: 0 }}>Assessment Sections (Term {currentTerm})</h3>
        
        <button
            type="button"
            className="btn btn-outline"
            style={{ fontSize: '1.1rem', padding: '2px 10px', width: 40, height: '32px', marginLeft: 8 }}
            onClick={() => setSectionsCollapsed(v => !v)}
            aria-label={sectionsCollapsed ? 'Expand Sections' : 'Collapse Sections'}
        >
            {sectionsCollapsed ? '+' : '-'}
            
        </button>
        
        
    </div>
    <p className="card-subtitle">Define the assessment structure first</p>
    

    {/* Add Section Bar */}
    {!sectionsCollapsed && (
        <>
        
            {/* --- NEW VISUAL STEPPER GUIDANCE --- */}
              {/* Copy All Assessments Button */}
                    {sections.length > 0 && !isFoundationSubject && (
                        <div style={{ marginTop: '12px' }}>
                            <button 
                                onClick={handleOpenCopyModal}
                                style={{
                                    padding: '8px 14px',
                                    background: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: '500',
                                    marginBottom: '10px'
                                }}
                            >
                                 Copy Sections to another Term
                            </button>
                        </div>
                    )}
            <div className="assessment-stepper-guidance">
                <div className="step-item">
                    <span className="step-circle">1</span>
                    <span className="step-label">Name Assessment</span>
                </div>
                
                <div className="step-connector"></div>
                
                <div className="step-item">
                    <span className="step-circle">2</span>
                    <span className="step-label">Set Weight</span>
                </div>
                
                <div className="step-connector"></div>
                
                <div className="step-item">
                    <span className="step-circle">3</span>
                    <span className="step-label">Add Sections</span>
                </div>
            </div>

            {isFoundationSubject ? (
                <div className="assessment-form-container" style={{ padding: '16px 18px', background: '#f8fbf5', border: '1px solid #dfe7d8', borderRadius: '12px', marginBottom: '18px' }}>
                    <strong style={{ display: 'block', marginBottom: '8px', color: '#3c5a42' }}>Foundation Phase template active</strong>
                    <p style={{ margin: 0, color: '#55655d' }}>
                        This subject uses a managed Foundation Phase layout. Marks can still be captured normally, but the assessment structure is generated automatically from the CAPS template for this term.
                    </p>
                </div>
            ) : (
                <form onSubmit={handleAddSection} className="assessment-form-container">
                    {/* --- ASSESSMENT LEVEL DETAILS --- */}
                    <fieldset className="form-fieldset">
                        <legend className="fieldset-legend">Assessment Details</legend>
                        <div className="form-grid-2">
                            <div className="stacked-field">
                                <label htmlFor="new-assessment-name">Assessment Name</label>
                                <input
                                    id="new-assessment-name"
                                    className="modern-input"
                                    placeholder="e.g. Term 1 Test"
                                    value={newSection.assessment_group}
                                    onChange={e => setNewSection({ ...newSection, assessment_group: e.target.value })}
                                />
                            </div>
                        </div>
                    </fieldset>

                    {/* --- SECTION LEVEL DETAILS --- */}
                    <fieldset className="form-fieldset mt-4">
                        <legend className="fieldset-legend">Add a New Section</legend>
                        <div className="form-grid-3">
                            <div className="stacked-field">
                                <label htmlFor="new-section-name">Section Name</label>
                                <input
                                    id="new-section-name"
                                    className="modern-input"
                                    placeholder="e.g. Question 1 or Language Structures"
                                    value={newSection.section_name}
                                    onChange={e => setNewSection({ ...newSection, section_name: e.target.value })}
                                />
                            </div>

                            <div className="stacked-field">
                                <label htmlFor="new-section-max">Max Mark</label>
                                <input
                                    id="new-section-max"
                                    type="number"
                                    className="modern-input"
                                    placeholder="Max"
                                    value={newSection.max_mark}
                                    onChange={e => setNewSection({ ...newSection, max_mark: e.target.value })}
                                />
                            </div>

                            <div className="stacked-field">
                                <label htmlFor="new-assessment-weight">Overall Weight</label>
                                <input
                                    id="new-assessment-weight"
                                    type="number"
                                    className="modern-input"
                                    placeholder="Weight"
                                    value={matchingAssessmentGroup ? matchingAssessmentGroup.currentWeight : newSection.weight}
                                    disabled={!!matchingAssessmentGroup}
                                    onChange={e => setNewSection({ ...newSection, weight: e.target.value })}
                                />
                                {matchingAssessmentGroup && (
                                    <small className="field-hint text-muted">
                                        Weight is locked to the existing assessment.
                                    </small>
                                )}
                            </div>

                            <div className="stacked-field">
                                <label htmlFor="new-section-category">Category</label>
                                <select
                                    id="new-section-category"
                                    className="modern-input"
                                    value={newSection.category}
                                    onChange={e => setNewSection({ ...newSection, category: e.target.value })}
                                >
                                    {SECTION_CATEGORIES.map(c => (
                                        <option key={c} value={c}>{c || 'Select Category'}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-actions mt-3">
                            <button type="submit" className="btn btn-primary" disabled={addingSection}>
                                {addingSection ? 'Adding Section...' : 'Add Section'}
                            </button>
                        </div>
                    </fieldset>
                </form>
            )}
        </>
                    )}

                    {/* Section List */}
                    <div className="assessment-group-list">
                        {!sectionsCollapsed && (
                            <>
                                {assessmentGroups.length === 0 && <p style={{ color: '#999', padding: '10px' }}>No sections for Term {currentTerm} yet.</p>}
                                {assessmentGroups.map((group) => {
                                    const managedWeight = foundationWeightsByLabel[String(group.label || '').toLowerCase()];
                                    const displayWeight = isFoundationSubject
                                        ? String(managedWeight ?? group.currentWeight)
                                        : (assessmentWeightDrafts[group.key] ?? String(group.currentWeight));
                                    const assessmentNameValue = assessmentNameDrafts[group.key] ?? group.primaryGroupName;

                                    return (
                                        <div key={group.key} className="assessment-group-card">
                                            <div className="assessment-group-header">
                                                <div className="assessment-group-title">
                                                    <h4>{group.label}</h4>
                                                    <div className="assessment-group-meta">
                                                        <span className="assessment-chip">{group.sections.length} section{group.sections.length === 1 ? '' : 's'}</span>
                                                        <span className="assessment-chip">Raw max {group.rawMax || 0}</span>
                                                        <span className="assessment-chip">{isFoundationSubject ? 'CAPS weight' : 'Weight'} {displayWeight}</span>
                                                        
                                                    </div>
                                                   
                                                </div>

                                                <div className="assessment-inline-editors">
                                                    <div className="assessment-inline-editor">
                                                        <label htmlFor={`assessment-name-${group.key}`}>Assessment Name</label>
                                                        <input
                                                            id={`assessment-name-${group.key}`}
                                                            className="modern-input"
                                                            value={assessmentNameValue}
                                                            disabled={isFoundationSubject || savingAssessmentNameKey === group.key}
                                                            onChange={(e) => setAssessmentNameDrafts(prev => ({ ...prev, [group.key]: e.target.value }))}
                                                            onBlur={() => {
                                                                if (!isFoundationSubject) {
                                                                    handleUpdateAssessmentGroupName(group);
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    e.currentTarget.blur();
                                                                }
                                                            }}
                                                            placeholder="e.g. Term 1 Test"
                                                        />
                                                      
                                                    </div>

                                                    <div className="assessment-inline-editor">
                                                        <label htmlFor={`assessment-weight-${group.key}`}>Assessment Weight</label>
                                                        <input
                                                            id={`assessment-weight-${group.key}`}
                                                            className="modern-input"
                                                            type="number"
                                                            min="1"
                                                            step="0.01"
                                                            value={displayWeight}
                                                            disabled={isFoundationSubject || savingAssessmentGroupKey === group.key}
                                                            onChange={(e) => setAssessmentWeightDrafts(prev => ({ ...prev, [group.key]: e.target.value }))}
                                                            onBlur={() => {
                                                                if (!isFoundationSubject) {
                                                                    handleUpdateAssessmentGroupWeight(group);
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    e.currentTarget.blur();
                                                                }
                                                            }}
                                                        />
                                                        <p className="helper-text">
                                                            {isFoundationSubject
                                                                ? 'CAPS weighting is controlled by the Foundation Phase template.'
                                                                : 'Weight is how much the task will count towards the end of the term'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="section-row-list">
                                                {group.sections.map((s) => (
                                                    <div key={s.id} className="section-row">
                                                        <div>
                                                            <p className="section-row-title">{s.section_name}</p>
                                                           
                                                        </div>
                                                        <div className="stacked-field">
                                                            <span>Max Mark</span>
                                                            <input
                                                                className="modern-input"
                                                                type="number"
                                                                value={sectionMaxMarks[s.id] ?? s.max_mark}
                                                                disabled={isFoundationSubject}
                                                                onChange={e => handleUpdateSection(s.id, 'max_mark', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="stacked-field">
                                                            <span>Category</span>
                                                            <select
                                                                className="modern-input"
                                                                value={s.category || ''}
                                                                disabled={isFoundationSubject}
                                                                onChange={e => handleUpdateSectionCategory(s.id, e.target.value)}
                                                            >
                                                                {SECTION_CATEGORIES.map(c => <option key={c} value={c}>{c || '-- None --'}</option>)}
                                                            </select>
                                                        </div>
                                                        {!isFoundationSubject && (
                                                            <button
                                                                className="btn-danger"
                                                                onClick={() => handleDeleteSection(s.id, s.section_name)}
                                                            >
                                                                Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>

                  
                </div>
            </div>
            </div>

            {/* Copy All Assessments Modal */}
            {showCopyModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(44,62,80,0.18)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        margin: '0 auto',
                        width: '100%',
                        maxWidth: '500px',
                        padding: '24px',
                        borderRadius: '14px',
                        background: '#fff',
                        position: 'relative',
                        border: '1.5px solid #4CAF50',
                        boxSizing: 'border-box',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0 }}>Copy All Assessments To...</h3>
                            <button onClick={() => setShowCopyModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#999' }}>x</button>
                        </div>
                        
                        <p style={{ color: '#666', marginBottom: '16px', fontSize: '0.95rem' }}>
                            Select a subject to copy all {sections.length} assessment(s) sections from <strong>{subjectname}</strong> {subjectClass && <span>({subjectClass})</span>}.
                        </p>
                        
                        {otherSubjects.length === 0 ? (
                            <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No other subjects available.</p>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                                {otherSubjects.map(subj => (
                                    <button
                                        key={subj.subject_id}
                                        onClick={() => {
                                            handleCopyAllAssessments(subj.subject_id);
                                        }}
                                        disabled={copyingSection}
                                        style={{
                                            padding: '12px 16px',
                                            background: '#f5f5f5',
                                            border: '1px solid #ddd',
                                            borderRadius: '8px',
                                            cursor: copyingSection ? 'not-allowed' : 'pointer',
                                            textAlign: 'left',
                                            fontSize: '0.95rem',
                                            opacity: copyingSection ? 0.6 : 1,
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <strong>{subj.name}</strong> {subj.class_name && <span>({subj.class_name})</span>} <span style={{ color: '#999', fontSize: '0.85rem' }}>Grade {subj.grade}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Main Data Table */}
            <div className="section-block">
            <div className="section-heading">
                <div>
                    <p className="section-kicker">Marks Entry</p>
                    <h2>Learner marks for Term {currentTerm}</h2>
                   
                </div>
                {!loading && (
                    <div className="section-actions no-print">
                        <button
                            className="btn btn-outline"
                            style={{ fontSize: '0.92rem', padding: '4px 14px', height: '32px', lineHeight: 1.2 }}
                            onClick={() => setShowTop10(v => !v)}
                        >
                            {showTop10 ? 'Hide' : 'Show'} Top 10 Learners
                            <img src="/assets/star.svg" alt="Trophy Icon" style={{ width: '16px', height: '16px', marginLeft: '6px', verticalAlign: 'middle',filter: 'invert(1)' }} />
                        </button>
                        <button
                            className="btn btn-outline"
                            style={{
                                fontSize: '0.92rem',
                                padding: '4px 14px',
                                height: '32px',
                                lineHeight: 1.2,
                                border: '1.5px solid #e74c3c',
                                color: '#ffffff',
                                background: '#e74c3c'
                            }}
                            onClick={() => setShowBottom10(v => !v)}
                        >
                            {showBottom10 ? 'Hide' : 'Show'} At Risk Learners
                            <img src="/assets/danger.svg" alt="Alert Icon" style={{ width: '16px', height: '16px', marginLeft: '6px', verticalAlign: 'middle', filter: 'invert(1)' }} />
                        </button>
                    </div>
                )}
            </div>
            {loading ? (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Loading Term {currentTerm} data...</p>
                </div>
            ) : (
                <>
                    <div className="table-container">
                        {/* Top 10 Modal */}
                        {showTop10 && (
                            <div style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                background: 'rgba(44,62,80,0.18)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <div className="stat_card" style={{
                                    margin: 0,
                                    maxWidth: '420px',
                                    minWidth: '260px',
                                    padding: '32px 32px 24px 32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '18px',
                                    boxShadow: '0 8px 32px rgba(44,62,80,0.18)',
                                    border: '1.5px solid var(--primary-green)',
                                    borderRadius: '18px',
                                    background: '#fff',
                                    position: 'relative',
                                }}>
                                    <button
                                        onClick={() => setShowTop10(false)}
                                        style={{
                                            position: 'absolute',
                                            top: 12,
                                            right: 12,
                                            background: 'none',
                                            border: 'none',
                                            fontSize: '1.5rem',
                                            color: '#aaa',
                                            cursor: 'pointer',
                                            zIndex: 2
                                        }}
                                        aria-label="Close Top 10"
                                    >
                                        x
                                    </button>
                                    <div className="icon_wrapper green" style={{ marginRight: '12px' }}>
                                        <Icons.Chart />
                                    </div>
                                    <div className="card_content" style={{ flex: 1 }}>
                                        <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-dark)', fontWeight: 700, letterSpacing: '0.2px', textAlign: 'center' }}>Top 10 Learners</h3>
                                        <ol style={{ margin: '18px 0 0 20px', padding: 0, textAlign: 'left' }}>
                                            {top10Students.map((s, i) => (
                                                <li key={s.student_id} style={{ marginBottom: '7px', fontWeight: 500, color: 'var(--text-grey)', fontSize: '1.05rem', display: 'flex', alignItems: 'center' }}>
                                                    <span style={{ color: 'var(--text-dark)', fontWeight: 600, minWidth: 32 }}>{i + 1}. {s.first_name} {s.last_name}</span>
                                                    <span style={{ marginLeft: 12, color: 'var(--primary-green)', fontWeight: 700, fontSize: '1.08rem' }}>
                                                        {s.average !== null ? `${Math.round(s.average)}%` : '-'}
                                                    </span>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* At Risk Learners Modal (55% and below) */}
                        {showBottom10 && (
                            <div style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                background: 'rgba(44,62,80,0.18)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                padding: '20px',
                                boxSizing: 'border-box',
                                overflowY: 'auto'
                            }}
                                onClick={() => setShowBottom10(false)}
                            >
                                <div className="stat_card" style={{
                                    margin: '0 auto',
                                    width: '100%',
                                    maxWidth: '420px',
                                    minWidth: '260px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    border: '1.5px solid #e74c3c',
                                    borderRadius: '18px',
                                    background: '#fff',
                                    position: 'relative',
                                    boxSizing: 'border-box',
                                    maxHeight: 'calc(100vh - 40px)',
                                    overflow: 'hidden'
                                }}
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <div style={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        background: '#fff',
                                        borderBottom: '1px solid #f3d4d0',
                                        padding: '20px 48px 16px 20px',
                                        textAlign: 'center'
                                    }}>
                                        <button
                                            onClick={() => setShowBottom10(false)}
                                            style={{
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '1.5rem',
                                                color: '#aaa',
                                                cursor: 'pointer',
                                                zIndex: 2
                                            }}
                                            aria-label="Close At Risk Learners"
                                        >
                                            x
                                        </button>
                                        <div className="icon_wrapper" style={{ color: '#e74c3c', marginBottom: '8px' }}>
                                            <Icons.Chart />
                                        </div>
                                        <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#e74c3c', fontWeight: 700, letterSpacing: '0.2px' }}>At Risk Learners (&lt;= 55%)</h3>
                                    </div>
                                    <div className="card_content" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 24px 20px' }}>
                                        {atRiskLearners.length === 0 ? (
                                            <div style={{ color: '#888', textAlign: 'center' }}>No learners at or below 55%.</div>
                                        ) : (
                                            <>
                                            <ol style={{ margin: '18px 0 0 20px', padding: 0, textAlign: 'left' }}>
                                                {atRiskLearners.map((s, i) => (
                                                    <li key={s.student_id} style={{ marginBottom: '7px', fontWeight: 500, color: 'var(--text-grey)', fontSize: '1.05rem', display: 'flex', alignItems: 'center' }}>
                                                        <span style={{ color: 'var(--text-dark)', fontWeight: 600, minWidth: 32 }}>{i + 1}. {s.first_name} {s.last_name}</span>
                                                        <span style={{ marginLeft: 12, color: '#e74c3c', fontWeight: 700, fontSize: '1.08rem' }}>
                                                            {s.average !== null ? `${Math.round(s.average)}%` : '-'}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ol>
                                            <div style={{ marginTop: 24, textAlign: 'center' }}>
                                                <button className="btn btn-outline" style={{ borderColor: '#e74c3c', color: '#e74c3c', fontWeight: 600 }}
                                                    onClick={() => { setShowResourceModal(true); fetchResourceSuggestions(); }}>
                                                    Show Suggested Interventions & Resources
                                                </button>
                                            </div>
                                            </>
                                        )}
                                    </div>
                                                        {/* Resource Suggestions Modal */}
                                                        {showResourceModal && (
                                                            <div style={{
                                                                position: 'fixed',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                background: 'rgba(44,62,80,0.18)',
                                                                zIndex: 2000,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                padding: '20px'
                                                            }}>
                                                                <div style={{
                                                                    margin: '0 auto',
                                                                    width: '100%',
                                                                    maxWidth: '680px',
                                                                    padding: '24px',
                                                                    borderRadius: '14px',
                                                                    background: '#fff',
                                                                    position: 'relative',
                                                                    border: '1.5px solid #e74c3c',
                                                                    boxSizing: 'border-box',
                                                                    maxHeight: '90vh',
                                                                    overflowY: 'auto'
                                                                }}>
                                                                    <button
                                                                        onClick={() => setShowResourceModal(false)}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: 12,
                                                                            right: 12,
                                                                            background: 'none',
                                                                            border: 'none',
                                                                            fontSize: '1.5rem',
                                                                            color: '#aaa',
                                                                            cursor: 'pointer',
                                                                            zIndex: 2
                                                                        }}
                                                                        aria-label="Close Resource Suggestions"
                                                                    >
                                                                        x
                                                                    </button>
                                                                    <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#e74c3c', fontWeight: 700, textAlign: 'center' }}>Suggested Interventions & Resources</h3>
                                                                    <div style={{ marginTop: 18 }}>
                                                                        {loadingResources ? (
                                                                            <div style={{ color: '#888', textAlign: 'center', margin: 16 }}>Loading resources...</div>
                                                                        ) : (
                                                                            resourceSuggestions.length === 0 ? (
                                                                                <div style={{ color: '#888', textAlign: 'center', margin: 16 }}>No resources found for this subject/grade.</div>
                                                                            ) : (
                                                                                <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                                                                                    {resourceSuggestions.map(res => (
                                                                                        <li key={res.id} style={{ marginBottom: 18, borderBottom: '1px solid #eee', paddingBottom: 10 }}>
                                                                                            <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.05rem' }}>{res.title}</div>
                                                                                            <div style={{ fontSize: '0.92rem', color: '#666', margin: '4px 0 6px 0' }}>Grade {res.grade_level} | {res.subject}</div>
                                                                                            <a href={`https://marksure.tech/${res.file_path}`} target="_blank" rel="noopener noreferrer" style={{ color: '#4CAF50', fontWeight: 600, textDecoration: 'underline', fontSize: '0.97rem' }}>Download Resource</a>
                                                                                        </li>
                                                                                    ))}
                                                                                </ul>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                </div>
                            </div>
                        )}
                        <div className="table-toolbar">
                            <div className="table-toolbar-copy">
                                <span className="table-toolbar-title">Term {currentTerm} Marks</span>
                                <span className="table-toolbar-note">
                                    {isFoundationSubject
                                        ? 'Foundation Phase columns are grouped by CAPS component, then by sub-section.'
                                        : hasGroupedColumnHeaders
                                            ? 'Columns are grouped by assessment or category, then by section.'
                                        : 'Search with Ctrl+F or Cmd+F.'}
                                </span>
                            </div>
                            <button className="btn btn-primary no-print" style={{ width: 'auto' }} onClick={handleBulkSave} disabled={saveAllDisabled}>
                                <Icons.Save /> {saveAllDisabled ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>



                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', paddingLeft: '1.5rem', width: '200px' }}>Student Name</th>
                                    {sections.map((s, index) => {
                                        const meta = sectionHeaderMeta[s.id];
                                        const previousMeta = index > 0 ? sectionHeaderMeta[sections[index - 1]?.id] : null;
                                        const isNewGroupedHeader = !!(meta?.showGroupLabel && (!previousMeta || previousMeta.groupKey !== meta.groupKey));

                                        return (
                                            <th
                                                key={s.id}
                                                style={{
                                                    background: meta?.tint,
                                                    borderLeft: isNewGroupedHeader ? '3px solid #cdd9ce' : undefined
                                                }}
                                            >
                                                {meta?.showGroupLabel ? (
                                                    <>
                                                        <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#597060', fontWeight: '700', marginBottom: '0.28rem' }}>
                                                            {meta.groupLabel}
                                                        </div>
                                                        <div style={{ fontSize: '0.92rem', lineHeight: 1.25 }}>{s.section_name}</div>
                                                    </>
                                                ) : (
                                                    <div style={{ fontSize: '0.95rem' }}>{s.section_name}</div>
                                                )}
                                                <div style={{ fontSize: '0.75rem', color: '#999', fontWeight: '400' }}>Max: {s.max_mark}</div>
                                            </th>
                                        );
                                    })}
                                    <th style={{ width: '100px', background: '#e8f5e9', borderLeft: '2px solid #dfe6e9' }}>Raw Total</th>
                                    {isFoundationSubject && (
                                        <th style={{ width: '100px', background: '#eef7f0' }}>CAPS Total</th>
                                    )}
                                    <th style={{ width: '80px', background: '#e8f5e9' }}>%</th>
                                    <th style={{ width: '60px', background: '#e8f5e9' }}>Lvl</th>
                                    <th className="no-print" style={{ width: '96px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {studentsWithComputed.map(student => (
                                    
                                    <tr key={student.student_id}>
                                        <td style={{ textAlign: 'left', paddingLeft: '1.5rem', fontWeight: '500' }}>
                                            {student.first_name} {student.last_name}
                                        </td>
                                        {sections.map((s, index) => {
                                            const key = `${student.student_id}-${s.id}`;
                                            const markObj = marksMap.get(key);
                                            const val = marksBeingEdited[key] !== undefined ? marksBeingEdited[key] : (markObj?.mark ?? '');
                                            const ref = getMarkInputRef(s.id, student.student_id);
                                            const meta = sectionHeaderMeta[s.id];
                                            const previousMeta = index > 0 ? sectionHeaderMeta[sections[index - 1]?.id] : null;
                                            const isNewGroupedHeader = !!(meta?.showGroupLabel && (!previousMeta || previousMeta.groupKey !== meta.groupKey));
                                           
                                            return (
                                                <td
                                                    key={s.id}
                                                    style={{
                                                        padding: 0,
                                                        background: meta?.tint,
                                                        borderLeft: isNewGroupedHeader ? '3px solid #cdd9ce' : undefined
                                                    }}
                                                >
                                                    <input
                                                        ref={ref}
                                                        className="cell-input"
                                                        value={val}
                                                        placeholder="-"
                                                        onChange={e => handleSectionMarkChange(student.student_id, s.id, e.target.value)}
                                                        onBlur={() => handleMarkBlur(student.student_id, s.id)}
                                                        onKeyDown={e => handleGridNavigation(e, student.student_id, s.id)}
                                                    />
                                                </td>
                                            );
                                        })}

                                        {/* Raw Total */}
                                        <td style={{ fontWeight: 'bold', color: '#555', background: '#f8f9fa', borderLeft: '2px solid #dfe6e9' }}>
                                            {student.rawMaxSum > 0 ? (
                                                <span>{Number(student.rawMarkSum).toFixed(1)} <span style={{ fontSize: '0.8em', color: '#999' }}>/ {student.rawMaxSum}</span></span>
                                            ) : '-'}
                                        </td>

                                        {isFoundationSubject && (
                                            <td style={{ fontWeight: 'bold', color: '#41644a', background: '#f3faf4' }}>
                                                {student.finalMaxSum > 0 ? (
                                                    <span>{Number(student.finalMarkSum).toFixed(1)} <span style={{ fontSize: '0.8em', color: '#7b8d80' }}>/ {student.finalMaxSum}</span></span>
                                                ) : '-'}
                                            </td>
                                        )}

                                        <td style={{ background: '#fcfcfc', fontWeight: 'bold', color: student.average !== null && student.average >= 50 ? 'var(--primary-green)' : '#e74c3c' }}>
                                            {student.average !== null ? Math.round(student.average) + '%' : '-'}
                                            {top10Ids.includes(student.student_id) && <span className="top-badge" title="Top 10">🌟</span>}
                                        </td>

                                        <td style={{ background: '#fcfcfc', fontWeight: 'bold' }}>
                                            <span style={{
                                                display: 'inline-block', width: '24px', height: '24px', lineHeight: '24px', borderRadius: '50%',
                                                background: student.level >= 4 ? 'var(--light-green)' : '#fee2e2',
                                                color: student.level >= 4 ? 'var(--dark-green)' : '#dc2626',
                                                fontSize: '0.85rem'
                                            }}>
                                                {formatGrade(student.level)}
                                            </span>
                                        </td>
                                        <td className="no-print">
                                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                                                <ISPGenerator learnerId={student.student_id} term={currentTerm} />
                                                <button className="btn-danger" style={{ borderRadius: '4px', padding: '4px 8px' }} onClick={() => handleDeleteStudent(student.student_id, student.first_name)}>Remove</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Performance Analytics (Dynamic per Term) */}
                    {sections.length > 0 && (
                        <div className="no-print" style={{ marginTop: '40px' }}>
                            <h3 className="stats-section-title"><Icons.Chart /> Term {currentTerm} Analytics</h3>
                            <div className="analytics-grid">
                                {sections.map(section => {
                                    const stats = getSectionStats(section);
                                    if (!stats) return null;

                                    return (
                                        <div key={section.id} className="stat-card">
                                            <h4>{section.section_name}</h4>
                                            <div className="stat-grid-inner">
                                                <div className="stat-item">
                                                    <span className="stat-label">Average Mark</span>
                                                    <span className="stat-value">{stats.avg.toFixed(1)}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Avg %</span>
                                                    <span className="stat-value green">{stats.avgPerc.toFixed(1)}%</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Highest</span>
                                                    <span className="stat-value">{stats.max}</span>
                                                </div>
                                                <div className="stat-item">
                                                    <span className="stat-label">Lowest</span>
                                                    <span className="stat-value red">{stats.min}</span>
                                                </div>
                                            </div>
                                            <div className="submission-count">
                                                <Icons.Check /> Submitted: <strong>{stats.count}</strong> / {students.length} students
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    
                </>
            )}
            </div>
        </div>
    );
}
